import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { isActivePaidEnrollment } from '../../config/sleepCareCheckout';
import { COHORT_CARE_OPTION_IDS } from '../../config/sleepCareConstants';
import {
  transcribeAudio,
  insertRelaxationListenEvent,
  requestCoachReply,
} from '../../lib/missionApi';
import { DashboardSurveyModal } from './dashboardSurveyModal';
import { DashboardChangeReportModal } from './DashboardChangeReportModal';
import './dashboard.css';

function IconRelaxation() {
  return (
    <svg
      className="db-tab-icon-svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.5 4.25v2.5M16.25 5.5h2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconMeet() {
  return (
    <svg
      className="db-tab-icon-svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconMy() {
  return (
    <svg
      className="db-tab-icon-svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSoundSkipBack() {
  return (
    <svg
      className="db-skip-icon"
      width="24"
      height="24"
      viewBox="0 -960 960 960"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M480-80q-75 0-140.5-28.5t-114-77q-48.5-48.5-77-114T120-440h80q0 117 81.5 198.5T480-160q117 0 198.5-81.5T760-440q0-117-81.5-198.5T480-720h-6l62 62-56 58-160-160 160-160 56 58-62 62h6q75 0 140.5 28.5t114 77q48.5 48.5 77 114T840-440q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-80ZM360-320v-180h-60v-60h120v240h-60Zm140 0q-17 0-28.5-11.5T460-360v-160q0-17 11.5-28.5T500-560h80q17 0 28.5 11.5T620-520v160q0 17-11.5 28.5T580-320h-80Zm20-60h40v-120h-40v120Z"
      />
    </svg>
  );
}

function IconSoundSkipForward() {
  return (
    <svg
      className="db-skip-icon"
      width="24"
      height="24"
      viewBox="0 -960 960 960"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M480-80q-75 0-140.5-28.5t-114-77q-48.5-48.5-77-114T120-440q0-75 28.5-140.5t77-114q48.5-48.5 114-77T480-800h6l-62-62 56-58 160 160-160 160-56-58 62-62h-6q-117 0-198.5 81.5T200-440q0 117 81.5 198.5T480-160q117 0 198.5-81.5T760-440h80q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-80Z"
      />
      <path
        fill="currentColor"
        d="M360-320v-180h-60v-60h120v240h-60Zm140 0q-17 0-28.5-11.5T460-360v-160q0-17 11.5-28.5T500-560h80q17 0 28.5 11.5T620-520v160q0 17-11.5 28.5T580-320h-80Zm20-60h40v-120h-40v120Z"
      />
    </svg>
  );
}

/* 구 JSX에서 `IconSkipBack10` / `IconSkipForward10` 참조 시 호환 */
const IconSkipBack10 = IconSoundSkipBack;
const IconSkipForward10 = IconSoundSkipForward;

/** 내 정보 · 설문 카드 — 사전(작성 전) / 사후(완료) */
function IconSurveyPreCard() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect x="6" y="4" width="12" height="16" rx="2" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M9 9h6M9 12h6M9 15h4"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconSurveyPostCard() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect x="5" y="3" width="14" height="18" rx="2" stroke="currentColor" strokeWidth="1.65" />
      <path d="M8 9h5M8 12h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="M8.5 17.5l2.5-2.5 2 2L17 12"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** 그룹챗 구성원 트리거 — 원형 머리 + 어깨 곡선·직선 측면·평바닥(하단 모서리 Q) */
function IconChatMembers() {
  return (
    <svg
      className="db-members-trigger-icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="12" cy="6.85" r="3.45" />
      <path d="M5 15Q12 11.65 19 15L19 21.15Q19 22.45 18 22.45H6Q5 22.45 5 21.15L5 15Z" />
    </svg>
  );
}

function IconSend() {
  return (
    <svg
      className="db-send-icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}

/** Lucide `mic` — https://github.com/lucide-icons/lucide/blob/main/icons/mic.svg (ISC) */
function IconMissionRecord({ size = 14, className } = {}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 19v3" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <rect x="9" y="2" width="6" height="13" rx="3" />
    </svg>
  );
}

/** Lucide `file-pen-line` — https://github.com/lucide-icons/lucide/blob/main/icons/file-pen-line.svg (ISC) */
function IconMissionFeedbackDoc({ size = 20, className } = {}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M14.364 13.634a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506l4.013-4.009a1 1 0 0 0-3.004-3.004z" />
      <path d="M14.487 7.858A1 1 0 0 1 14 7V2" />
      <path d="M20 19.645V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l2.516 2.516" />
      <path d="M8 18h1" />
    </svg>
  );
}

const TABS = [
  { id: 'home', label: '이완훈련', Icon: IconRelaxation },
  { id: 'meet', label: '그룹챗', Icon: IconMeet },
  { id: 'my', label: '내 정보', Icon: IconMy },
];

/** 이완 사운드 주차별 목업 — 제목·설명·썸네일 (16:9) */
const RELAXATION_WEEKS = [
  {
    id: 1,
    title: '몸 풀기 호흡',
    description: '잠들기 전 긴장을 낮추는 호흡 가이드와 부드러운 바람 소리예요.',
    durationLabel: '18:24',
    thumb: 'https://picsum.photos/seed/sc-relax-w1/640/360',
    cohortRelaxationWeeksId: null,
  },
  {
    id: 2,
    title: '수면 루틴 스캐폴드',
    description: '침대에 누운 뒤 몸을 인지하고 천천히 이완하는 단계별 안내예요.',
    durationLabel: '22:10',
    thumb: 'https://picsum.photos/seed/sc-relax-w2/640/360',
    cohortRelaxationWeeksId: null,
  },
  {
    id: 3,
    title: '백색 소음 & 레인',
    description: '외부 소음을 덮는 빗소리·백색 소음 믹스로 수면 환경을 맞춰요.',
    durationLabel: '45:00',
    thumb: 'https://picsum.photos/seed/sc-relax-w3/640/360',
    cohortRelaxationWeeksId: null,
  },
  {
    id: 4,
    title: '심화 이완 내레이션',
    description: '긴 호흡과 시각화로 깊은 이완에 들어가는 긴 코스예요.',
    durationLabel: '32:05',
    thumb: 'https://picsum.photos/seed/sc-relax-w4/640/360',
    cohortRelaxationWeeksId: null,
  },
];

const durationLabelToSeconds = (label) => {
  const [mm = '0', ss = '0'] = String(label ?? '0:0').split(':');
  const minutes = Number(mm);
  const seconds = Number(ss);
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return 0;
  return minutes * 60 + seconds;
};

const formatSecondsLabel = (totalSeconds) => {
  const safe = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const minutes = Math.floor(safe / 60);
  const seconds = String(safe % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const durationSecondsToLabel = (seconds) => {
  const safe = Math.max(0, Math.floor(Number(seconds) || 0));
  const mm = Math.floor(safe / 60);
  const ss = String(safe % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

/** profiles.birth (date / string) → <input type="date" /> 값 */
function profileBirthToDateInputValue(b) {
  if (b == null || b === '') return '';
  if (b instanceof Date) return b.toISOString().slice(0, 10);

  const raw = String(b).trim();
  if (!raw) return '';

  const isoLike = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoLike) return isoLike[1];

  // "YYYY.MM.DD", "YYYY/MM/DD", "YYYYMMDD" 입력도 date input 값으로 정규화
  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 8) {
    const y = digits.slice(0, 4);
    const m = digits.slice(4, 6);
    const d = digits.slice(6, 8);
    const mm = Number(m);
    const dd = Number(d);
    if (mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      return `${y}-${m}-${d}`;
    }
  }

  // 형식이 맞지 않으면 빈 값으로 처리해 6자리 연도 등 비정상 값 노출 방지
  return '';
}

function phoneOnlyDigits(s) {
  return String(s ?? '').replace(/\D/g, '');
}

/**
 * integer DB 등으로 앞 0이 빠진 10자리(0108… → 108…)를 11자리로 복원.
 * (010/011/016~019 가운데 0이 사라지면 두 번째~세 번째 자리가 10,11,16~19로 남는 패턴만 보정)
 */
function normalizeKoreanPhoneDigits(s) {
  let d = phoneOnlyDigits(s).slice(0, 11);
  if (d.length === 10 && /^(10|11|16|17|18|19)/.test(d)) {
    d = `0${d}`;
  }
  return d.slice(0, 11);
}

/** 휴대폰(010…) 입력 중·11자리 → `010-0000-0000` (3-4-4) */
function formatKoreanPhoneDisplay(digits) {
  const d = normalizeKoreanPhoneDigits(digits);
  if (d.length === 0) return '';
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
}

/**
 * DB `profiles.phone` 저장용 — `010-0000-0000` (하이픈·앞 0 포함 문자열, 컬럼은 text/varchar).
 */
function phoneFormattedForDb(phoneState) {
  const d = normalizeKoreanPhoneDigits(phoneState);
  if (!d) return null;
  return formatKoreanPhoneDisplay(d);
}

/** 저장·게이트와 동일 기준(닉네임·성별·생일·휴대폰·4주목표) */
function isProfileFormComplete(n, gender, b, p, goal) {
  if (!n?.trim()) return false;
  if (!gender || !['f', 'm', 'x'].includes(gender)) return false;
  if (!b?.trim()) return false;
  if (!phoneOnlyDigits(p)) return false;
  if (!goal?.trim()) return false;
  return true;
}

/** 미션 녹음: 2026년 4월 6일(월) 시작 · 4주(28일) */
const MISSION_START = new Date(2026, 3, 6);
const MISSION_END = new Date(2026, 4, 3);
const MISSION_WEEKDAY_HEADERS = ['월', '화', '수', '목', '금', '토', '일'];
const DAY_MS = 24 * 60 * 60 * 1000;

function defaultMissionDateLine() {
  return `${MISSION_START.getFullYear()}년 ${MISSION_START.getMonth() + 1}월 ${MISSION_START.getDate()}일 ~ ${MISSION_END.getMonth() + 1}월 ${MISSION_END.getDate()}일`;
}

function formatCohortDateLineFromOption(startAt, endAt) {
  if (startAt == null || endAt == null) return null;
  const s = new Date(startAt);
  const e = new Date(endAt);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  const sy = s.getFullYear();
  const sm = s.getMonth() + 1;
  const sd = s.getDate();
  const ey = e.getFullYear();
  const em = e.getMonth() + 1;
  const ed = e.getDate();
  const startStr = `${sy}년 ${sm}월 ${sd}일`;
  if (sy === ey) {
    return `${startStr} ~ ${em}월 ${ed}일`;
  }
  return `${startStr} ~ ${ey}년 ${em}월 ${ed}일`;
}

/** `cohort_care_options` 한 행 → 헤더 1·2행 문구 (cohort_label 미사용) */
function cohortOptionToHeaderLabels(c) {
  if (!c) return null;
  const n = c.cohort_number;
  const hasNum = n != null && String(n).trim() !== '' && !Number.isNaN(Number(n));
  const roundLabel = hasNum ? `${Number(n)}기` : '—';
  const careTitle = c.title || '—';
  const dateLine = formatCohortDateLineFromOption(c.start_at, c.end_at) ?? defaultMissionDateLine();
  return { roundLabel, careTitle, dateLine };
}

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const toDateKey = (d) => {
  const x = startOfDay(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseDateKey = (key) => {
  const [yy, mm, dd] = String(key).split('-').map(Number);
  return new Date(yy, mm - 1, dd);
};

const defaultSelectedMissionDateKey = (missionStart, missionEnd) => {
  const t = startOfDay(new Date());
  const s = startOfDay(missionStart);
  const e = startOfDay(missionEnd);
  if (t >= s && t <= e) return toDateKey(t);
  return toDateKey(s);
};

const formatMissionDateLabel = (dateKey) => {
  const dt = parseDateKey(dateKey);
  const m = dt.getMonth() + 1;
  const d = dt.getDate();
  const w = ['일', '월', '화', '수', '목', '금', '토'][dt.getDay()];
  return `${m}월 ${d}일 (${w})`;
};

const buildMissionCalendarCells = (missionStart, missionEnd) => {
  const s = startOfDay(missionStart);
  const e = startOfDay(missionEnd);
  const monday = new Date(s);
  monday.setDate(s.getDate() - ((s.getDay() + 6) % 7));
  const sunday = new Date(e);
  sunday.setDate(e.getDate() + (6 - ((e.getDay() + 6) % 7)));

  const cells = [];
  const cur = new Date(monday);
  const end = new Date(sunday);
  let prevMonth = null;
  let prevYear = null;
  while (cur <= end) {
    const c = startOfDay(cur);
    const key = toDateKey(c);
    const inMission = c >= s && c <= e;
    const month = c.getMonth() + 1;
    const y = c.getFullYear();
    const monthChange = prevMonth === null || month !== prevMonth || y !== prevYear;
    prevMonth = month;
    prevYear = y;
    cells.push({
      dateKey: key,
      day: c.getDate(),
      month,
      monthChange,
      inMission,
    });
    cur.setDate(cur.getDate() + 1);
  }
  return cells;
};

const missionDateKeyByWeekDayIndex = (missionStart, weekIndex, dayInWeek) => {
  const dt = new Date(missionStart);
  dt.setDate(missionStart.getDate() + weekIndex * 7 + dayInWeek);
  return toDateKey(dt);
};

/** 모바일 호환 MIME 타입 결정 (iOS Safari = audio/mp4, 그 외 = audio/webm) */
const getSupportedMimeType = () => {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', ''];
  return types.find((t) => t === '' || MediaRecorder.isTypeSupported(t)) || '';
};

/** 녹음 최대 시간 (CLOVA Short Sentence 60초 제한 → 55초 안전 마진) */
const MAX_RECORDING_SECONDS = 55;

/** 모임 구성원 목업 — 추후 DB의 nickname + avatar_url 과 동일 구조 */
/** 기수·케어 목업 — 추후 cohort / enrollment 연동 */
const MOCK_COHORT_OPTIONS = [
  {
    id: '1',
    roundLabel: '1기',
    careTitle: '‘잠들기 전 복잡한 생각’ 케어',
  },
  // {
  //   id: '2',
  //   roundLabel: '2기',
  //   careTitle: '‘자는 동안 답답한 호흡’ 케어',
  //   participating: false,
  // },
  // {
  //   id: '3',
  //   roundLabel: '3기',
  //   careTitle: '‘한밤중 잦은 화장실’ 케어',
  //   participating: false,
  // },
];

/**
 * 그룹 인원 표시: cohort_care_options.id → checkouts.cohort_care_option_id → profiles
 * 아래 user_id에 한해 UI에 코치/매니저 뱃지.
 */
const COHORT_STAFF_PINS = [
  { userId: '49a5ff7a-37c0-456d-bf3a-19941711c577', role: 'master_coach' },
  // { userId: '5256b28b-34b3-46b4-a6f5-a7cd18873805', role: 'coach' },
  { userId: '7052f27f-cd55-40cd-9e64-209e211d6803', role: 'coach' },
  { userId: '391ac297-a111-42cd-84cc-91e4eb02e478', role: 'coach' },
  { userId: '51ae81ba-1946-403b-ad8c-b6b8202f80d5', role: 'manager' },
  { userId: '51262ff9-7c88-4485-ae78-e339138346c9', role: 'manager' },
];
const COHORT_STAFF_PIN_UID_SET = new Set(COHORT_STAFF_PINS.map((p) => p.userId));
const STAFF_ROLE_BY_USER_ID = new Map(COHORT_STAFF_PINS.map((p) => [p.userId, p.role]));

/**
 * 미션 녹음 게이트 우회 사용자 — 녹음 결과(transcript) 유무와 무관하게
 * 텍스트 수정·공유를 허용. 데모/시연용.
 */
const MISSION_FREE_EDIT_USER_IDS = new Set([
  'c4efe491-a21f-4b8d-a297-c373fa8b8e3b',
  '51262ff9-7c88-4485-ae78-e339138346c9',
  'fb0201d7-48e0-4eb6-9531-62ff03de9272',
]);

function defaultGroupMemberAvatarUrl(userId) {
  return `https://picsum.photos/seed/${String(userId).replace(/-/g, '').slice(0, 12)}/96/96`;
}

/**
 * 그룹챗 인원: public.cohort_care_options.id(UUID) → 그 id로 public.checkouts 필터 → auth_user_id → public.profiles
 * ① cohort 테이블에서 uuid 확인(없으면 RLS 등으로 못 읽은 것이므로 전달받은 값으로 ② 진행)
 */
async function buildGroupMembersFromCohortOption(client, cohortOptionUuid) {
  if (!cohortOptionUuid) return [];

  const { data: cohortRow, error: cohortErr } = await client
    .from('cohort_care_options')
    .select('*')
    .eq('id', cohortOptionUuid)
    .maybeSingle();
  if (cohortErr) {
    console.error('[dashboard] cohort_care_options', cohortErr);
  }
  const cohortId = cohortRow?.id ?? cohortOptionUuid;

  const { data: checkoutRows, error: chErr } = await client
    .from('checkouts')
    .select(
      'id, created_at, auth_user_id, cohort_care_option_id, payment_status, refund_status, payment_amount, refund_amount'
    )
    .eq('cohort_care_option_id', cohortId);
  if (chErr) {
    console.error('[dashboard] checkouts (그룹 인원)', chErr);
    return [];
  }

  const seen = new Set();
  const userIds = [];
  for (const row of checkoutRows || []) {
    if (!row?.auth_user_id) continue;
    if (seen.has(row.auth_user_id)) continue;
    seen.add(row.auth_user_id);
    userIds.push(row.auth_user_id);
  }
  if (!userIds.length) return [];

  const cohortSet = new Set(userIds);
  const topStaffUids = COHORT_STAFF_PINS.map((p) => p.userId).filter((uid) => cohortSet.has(uid));
  const otherUids = userIds.filter((uid) => !COHORT_STAFF_PIN_UID_SET.has(uid));
  const orderedUids = [...topStaffUids, ...otherUids];

  const { data: profiles, error: profErr } = await client
    .from('profiles')
    .select('id, nickname, avatar_url')
    .in('id', orderedUids);
  if (profErr) {
    console.error('[dashboard] profiles (그룹 인원)', profErr);
  }

  const pmap = new Map((profiles || []).map((p) => [p.id, p]));
  return orderedUids.map((userId) => {
    const p = pmap.get(userId);
    const rawNick = p?.nickname != null ? String(p.nickname).trim() : '';
    const role = STAFF_ROLE_BY_USER_ID.get(userId);
    return {
      id: userId,
      name: rawNick || '솜니아',
      avatar_url: p?.avatar_url || defaultGroupMemberAvatarUrl(userId),
      role: role || undefined,
    };
  });
}

/** ISO 문자열 → 한국어 시간 라벨 (예: 3월 27일 오후 2:30) */
function formatChatMessageTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ko-KR', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const truncateChatPreview = (text, maxLen = 80) => {
  const t = String(text ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!t) return '';
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('home');
  const fallbackMissionStart = useMemo(() => startOfDay(MISSION_START), []);
  const fallbackMissionEnd = useMemo(() => startOfDay(MISSION_END), []);

  /* ── 홈: 사운드 주차별 (목업) — Week1만 펼침, 2~4는 접힘 */
  const [relaxationWeeks, setRelaxationWeeks] = useState(RELAXATION_WEEKS);
  const [soundWeekOpen, setSoundWeekOpen] = useState(() => [true, false, false, false]);
  const [soundWeekState, setSoundWeekState] = useState(() =>
    RELAXATION_WEEKS.map((_, i) => ({
      locked: true,
      playing: false,
      volume: 72,
      progress: i === 0 ? 35 : 0,
    }))
  );
  const soundWeekStateRef = useRef(soundWeekState);
  soundWeekStateRef.current = soundWeekState;
  /** `loadedmetadata` 기준 실제 오디오 길이(초); 없으면 durationLabel로 표시 */
  const [weekMediaDuration, setWeekMediaDuration] = useState([0, 0, 0, 0]);
  const soundWeekAudioMapRef = useRef(new Map());

  const ensureSoundWeekAudio = (index) => {
    const m = soundWeekAudioMapRef.current;
    if (!m.has(index)) {
      const a = new Audio();
      a.preload = 'metadata';
      m.set(index, a);
    }
    return m.get(index);
  };

  const patchSoundWeek = (index, patch) => {
    setSoundWeekState((prev) => prev.map((s, j) => (j === index ? { ...s, ...patch } : s)));
  };

  const resolveTotalSeconds = (index, wk) => {
    if (!wk) return 0;
    const a = soundWeekAudioMapRef.current.get(index);
    if (wk.audioUrl && a && Number.isFinite(a.duration) && a.duration > 0) {
      return a.duration;
    }
    const fromMeta = weekMediaDuration[index];
    if (fromMeta > 0) return fromMeta;
    return durationLabelToSeconds(wk.durationLabel);
  };

  const seekSoundWeekSeconds = (index, deltaSeconds) => {
    const wk = relaxationWeeks[index];
    if (!wk) return;
    const a = soundWeekAudioMapRef.current.get(index);
    const labelTotal = durationLabelToSeconds(wk.durationLabel);
    const total =
      wk.audioUrl && a && Number.isFinite(a.duration) && a.duration > 0
        ? a.duration
        : weekMediaDuration[index] > 0
          ? weekMediaDuration[index]
          : labelTotal;
    if (!Number.isFinite(total) || total <= 0) return;

    /* `currentTime` 은 setState 밖에서만 — React 18 Strict Mode가 updater를 2번 호출해도 1회만 시크 */
    if (a && wk.audioUrl) {
      const cur = Number.isFinite(a.currentTime) ? a.currentTime : 0;
      const nextSec = Math.min(total, Math.max(0, cur + deltaSeconds));
      a.currentTime = nextSec;
      const nextProgress = Math.round((nextSec / total) * 100);
      setSoundWeekState((prev) => {
        if (!prev[index]) return prev;
        return prev.map((x, j) =>
          j === index ? { ...x, progress: Math.min(100, Math.max(0, nextProgress)) } : x
        );
      });
      return;
    }

    setSoundWeekState((prev) => {
      const s = prev[index];
      if (!s) return prev;
      const cur = (s.progress / 100) * total;
      const nextSec = Math.min(total, Math.max(0, cur + deltaSeconds));
      const nextProgress = Math.round((nextSec / total) * 100);
      return prev.map((x, j) =>
        j === index ? { ...x, progress: Math.min(100, Math.max(0, nextProgress)) } : x
      );
    });
  };

  const toggleSoundWeekPlay = (index) => {
    const wk = relaxationWeeks[index];
    if (!wk?.audioUrl || !String(wk.audioUrl).trim()) return;
    const st = soundWeekStateRef.current[index];
    if (!st) return;
    const willPlay = !st.playing;
    // 프로그램 기간 종료 후에는 새로 재생 시작 불가 (재생 중이면 정지는 허용)
    if (willPlay && isProgramExpired) {
      window.alert('프로그램 기간이 종료되어 이완 콘텐츠를 재생할 수 없어요. 기간을 연장하면 다시 이용할 수 있어요.');
      return;
    }
    if (willPlay) {
      soundWeekAudioMapRef.current.forEach((audio, j) => {
        if (j !== index) audio.pause();
      });
    }
    setSoundWeekState((prev) =>
      prev.map((s, j) => (j === index ? { ...s, playing: willPlay } : { ...s, playing: false }))
    );
    const a = ensureSoundWeekAudio(index);
    if (willPlay) {
      a.play().catch(() => {
        setSoundWeekState((p) => p.map((s, j) => (j === index ? { ...s, playing: false } : s)));
      });
    } else {
      a.pause();
    }
  };

  const toggleSoundWeekOpen = (index) => {
    setSoundWeekOpen((prev) => prev.map((v, j) => (j === index ? !v : v)));
  };

  /* ── 홈: 미션·녹음 (목업) — 4/6 시작, 날짜별 녹음·텍스트·제출 ── */
  const [selectedMissionDateKey, setSelectedMissionDateKey] = useState(() =>
    defaultSelectedMissionDateKey(fallbackMissionStart, fallbackMissionEnd)
  );
  const [missionCompleteByDate, setMissionCompleteByDate] = useState({});
  const [missionRecordedByDate, setMissionRecordedByDate] = useState({});
  const [missionTextByDate, setMissionTextByDate] = useState({});
  /** 날짜별 — 3단계 '텍스트로 전환' 클릭 후에만 4단계 입력 허용 */
  const [missionTextUnlockedByDate, setMissionTextUnlockedByDate] = useState({});
  /** 텍스트 '수정' 모드(완료 전만) — 날짜키별 */
  const [missionTextEditingByDate, setMissionTextEditingByDate] = useState({});
  const [recState, setRecState] = useState('idle'); // idle | recording | paused | textReady

  /* ── 음성 데이터 동의 ── */
  const [voiceConsent, setVoiceConsent] = useState(null); // null=로딩, true=동의함, false=미동의
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  /* ── 실제 녹음 관련 state/ref ── */
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recTimerRef = useRef(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioR2Key, setAudioR2Key] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState('');
  const [recSeconds, setRecSeconds] = useState(0);

  /* ── 모임: 대화 (목업) ── */
  const [chatInput, setChatInput] = useState('');
  /** 답글 대상 — id·이름·인용 미리보기(전송 시 replyTo 로 저장) */
  const [replyingTo, setReplyingTo] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false); // 그룹챗/AI 슬립코치 메시지 로딩 중
  const [chatSending, setChatSending] = useState(false);
  /** AI 슬립코치 응답 대기 상태 (typing 인디케이터 + 재시도 토스트) */
  const [coachReplyPending, setCoachReplyPending] = useState(false);
  const [coachReplyError, setCoachReplyError] = useState(null);
  /** 마지막 코치 요청 페이로드 (재시도용) */
  const lastCoachRequestRef = useRef(null);
  const chatScrollEndRef = useRef(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  /** 기수(cohort_care_option)별 그룹챗 구성원 — 상단 4인 고정 + 나머지 */
  const [groupMembersList, setGroupMembersList] = useState([]);

  /* ── 제출 확인 모달 ── */
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  /* ── 공유 후 AI 슬립코치 탭 이동 안내 모달 (SELF 코호트 한정) ── */
  const [coachRedirectModalOpen, setCoachRedirectModalOpen] = useState(false);
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  const [surveyModalPhase, setSurveyModalPhase] = useState('pre');
  const [changeReportModalOpen, setChangeReportModalOpen] = useState(false);
  const [changeReportPhase, setChangeReportPhase] = useState('pre');
  const [isPreSurveyCompleted, setIsPreSurveyCompleted] = useState(false);
  const [isPostSurveyCompleted, setIsPostSurveyCompleted] = useState(false);
  const [isPreSurveyCompletionLoading, setIsPreSurveyCompletionLoading] = useState(true);
  const [isProfileStatusLoading, setIsProfileStatusLoading] = useState(true);
  const [hasAppliedInitialGateTab, setHasAppliedInitialGateTab] = useState(false);
  const [weekInfoModal, setWeekInfoModal] = useState({
    open: false,
    title: '',
    information: '',
  });

  /** checkouts에 본인 auth_user_id 행이 있을 때만 대시보드 UI 표시 */
  const [enrollmentState, setEnrollmentState] = useState('loading');

  /* ── 내 계정 (목업) ── */
  const [nickname, setNickname] = useState('');
  const [gender, setGender] = useState('');
  const [birth, setBirth] = useState('');
  const [phone, setPhone] = useState('');
  const [fourWeekChangeGoal, setFourWeekChangeGoal] = useState('');

  /** `profiles.avatar_url` · 없으면 OAuth `user.user_metadata.avatar_url` */
  const [profileAvatarUrl, setProfileAvatarUrl] = useState(null);
  const [profileSaveBusy, setProfileSaveBusy] = useState(false);

  const [profileErrors, setProfileErrors] = useState({
    nickname: null,
    gender: null,
    birth: null,
    phone: null,
    fourWeekGoal: null,
  });

  /** 헤더 드롭다운: 유효 결제 checkout별 cohort_care_options (없으면 목업) */
  const [cohortMenuItems, setCohortMenuItems] = useState([]);
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [cohortMenuOpen, setCohortMenuOpen] = useState(false);
  const cohortWrapRef = useRef(null);

  useEffect(() => {
    if (!cohortMenuOpen) return;
    const onDown = (e) => {
      if (cohortWrapRef.current && !cohortWrapRef.current.contains(e.target)) {
        setCohortMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [cohortMenuOpen]);

  /** 헤더 `cohortCareOptionId` = `cohort_care_options.id` (= checkouts.cohort_care_option_id FK) */
  const selectedCohortItem = useMemo(() => {
    if (!cohortMenuItems.length) return null;
    return cohortMenuItems.find((x) => x.id === selectedCohortId) ?? cohortMenuItems[0];
  }, [cohortMenuItems, selectedCohortId]);

  const selectedCohortCareOptionId = selectedCohortItem?.cohortCareOptionId ?? null;
  const isSelfCohortSelected =
    String(selectedCohortItem?.cohortLabel || '').toUpperCase() === 'SELF';

  /** 코호트 종료일(end_at)이 지나면 만료 — 이완 재생 차단, AI 슬립코치(SELF) 대화 차단 */
  const isProgramExpired = useMemo(() => {
    const raw = selectedCohortItem?.missionEndAt;
    const endDay = raw ? startOfDay(new Date(raw)) : startOfDay(MISSION_END);
    if (Number.isNaN(endDay.getTime())) return false;
    return startOfDay(new Date()).getTime() > endDay.getTime();
  }, [selectedCohortItem]);
  /** AI 슬립코치(SELF) 대화 차단 여부 — 그룹챗은 만료돼도 계속 가능 */
  const isCoachExpired = isProgramExpired && isSelfCohortSelected;
  const tabItems = useMemo(
    () =>
      TABS.map((tabItem) =>
        tabItem.id === 'meet' && isSelfCohortSelected
          ? { ...tabItem, label: 'AI 슬립코치' }
          : tabItem
      ),
    [isSelfCohortSelected]
  );
  const listenSegRef = useRef(new Map());
  /** 동일 오디오 blob에 대해 STT가 중복 호출되지 않도록 */
  const missionTranscribeAttemptKeyRef = useRef(null);

  const flushRelaxListenSegment = useCallback((weekListIndex, endReason, opts = {}) => {
    const rec = listenSegRef.current.get(weekListIndex);
    if (!rec) return;
    listenSegRef.current.delete(weekListIndex);
    let sec = (Date.now() - rec.t0) / 1000;
    if (rec.durationCap != null) {
      sec = Math.min(sec, rec.durationCap);
    }
    if (sec < 0.25) return;
    if (!rec.cohortRelaxationWeeksId) return;
    void insertRelaxationListenEvent(
      {
        cohortRelaxationWeeksId: rec.cohortRelaxationWeeksId,
        secondsListened: sec,
        endReason,
      },
      { keepalive: Boolean(opts.keepalive) }
    );
  }, []);

  useEffect(() => {
    if (!selectedCohortCareOptionId || !currentUserId) {
      setIsPreSurveyCompleted(false);
      setIsPostSurveyCompleted(false);
      setIsPreSurveyCompletionLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsPreSurveyCompletionLoading(true);
      const { data: cohortInstruments, error: cohortErr } = await supabase
        .from('cohort_survey_instruments')
        .select('instrument_id')
        .eq('cohort_care_option_id', selectedCohortCareOptionId);
      if (cancelled) return;
      if (cohortErr) {
        console.error('[dashboard cohort_survey_instruments]', cohortErr);
        setIsPreSurveyCompleted(false);
        setIsPostSurveyCompleted(false);
        setIsPreSurveyCompletionLoading(false);
        return;
      }

      let requiredInstrumentIds = (cohortInstruments || [])
        .map((r) => r.instrument_id)
        .filter(Boolean);

      if (!requiredInstrumentIds.length) {
        const { data: activeInstruments, error: activeErr } = await supabase
          .from('survey_instruments')
          .select('id')
          .eq('is_active', true);
        if (cancelled) return;
        if (activeErr) {
          console.error('[dashboard survey_instruments active]', activeErr);
          setIsPreSurveyCompleted(false);
          setIsPostSurveyCompleted(false);
          setIsPreSurveyCompletionLoading(false);
          return;
        }
        requiredInstrumentIds = (activeInstruments || []).map((r) => r.id).filter(Boolean);
      }

      if (!requiredInstrumentIds.length) {
        setIsPreSurveyCompleted(false);
        setIsPostSurveyCompleted(false);
        setIsPreSurveyCompletionLoading(false);
        return;
      }

      const { data: submissions, error: subErr } = await supabase
        .from('survey_submissions')
        .select('instrument_id, phase')
        .eq('user_id', currentUserId)
        .eq('cohort_care_option_id', selectedCohortCareOptionId)
        .in('phase', ['pre', 'post'])
        .in('instrument_id', requiredInstrumentIds);
      if (cancelled) return;
      if (subErr) {
        console.error('[dashboard survey_submissions pre/post]', subErr);
        setIsPreSurveyCompleted(false);
        setIsPostSurveyCompleted(false);
        setIsPreSurveyCompletionLoading(false);
        return;
      }

      const preSet = new Set(
        (submissions || [])
          .filter((r) => r.phase === 'pre')
          .map((r) => r.instrument_id)
          .filter(Boolean)
      );
      const postSet = new Set(
        (submissions || [])
          .filter((r) => r.phase === 'post')
          .map((r) => r.instrument_id)
          .filter(Boolean)
      );
      setIsPreSurveyCompleted(requiredInstrumentIds.every((id) => preSet.has(id)));
      setIsPostSurveyCompleted(requiredInstrumentIds.every((id) => postSet.has(id)));
      setIsPreSurveyCompletionLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCohortCareOptionId, currentUserId, surveyModalOpen]);

  useEffect(() => {
    if (!selectedCohortCareOptionId) {
      setRelaxationWeeks(RELAXATION_WEEKS);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('cohort_relaxation_weeks')
        .select(
          'id, week_index, title, description, thumb_url, duration_seconds, audio_url, information'
        )
        .eq('cohort_care_option_id', selectedCohortCareOptionId)
        .order('week_index', { ascending: true });
      if (cancelled) return;
      if (error || !data?.length) {
        if (error) console.error('[dashboard cohort_relaxation_weeks]', error);
        setRelaxationWeeks(RELAXATION_WEEKS);
        return;
      }
      const mapped = data.map((row) => ({
        id: row.week_index,
        cohortRelaxationWeeksId: row.id,
        title: row.title || `Week ${row.week_index}`,
        description: row.description || '',
        durationLabel: durationSecondsToLabel(row.duration_seconds),
        thumb: row.thumb_url || `https://picsum.photos/seed/sc-relax-w${row.week_index}/640/360`,
        audioUrl: row.audio_url || null,
        information: row.information || '',
      }));
      setRelaxationWeeks(mapped);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCohortCareOptionId]);

  useEffect(() => {
    setSoundWeekState((prev) =>
      relaxationWeeks.map((w, i) => ({
        locked: prev[i]?.locked ?? true,
        playing: prev[i]?.playing ?? false,
        volume: prev[i]?.volume ?? 72,
        progress: prev[i]?.progress ?? (i === 0 ? 35 : 0),
      }))
    );
  }, [relaxationWeeks]);

  useEffect(() => {
    setWeekMediaDuration((prev) => relaxationWeeks.map((_, i) => prev[i] ?? 0));
  }, [relaxationWeeks.length]);

  useEffect(() => {
    const sameSrc = (audioEl, url) => {
      if (!url) return !audioEl?.src;
      try {
        return audioEl.src === new URL(url, window.location.href).href;
      } catch {
        return audioEl.src === url;
      }
    };
    const cleanups = [];
    relaxationWeeks.forEach((w, i) => {
      const a = ensureSoundWeekAudio(i);
      const url = w.audioUrl ? String(w.audioUrl).trim() : '';
      if (url) {
        if (!sameSrc(a, url)) {
          a.pause();
          a.currentTime = 0;
          a.src = url;
          a.load();
          setWeekMediaDuration((prev) => {
            const next = relaxationWeeks.map((_, j) => prev[j] ?? 0);
            next[i] = 0;
            return next;
          });
          setSoundWeekState((prev) =>
            prev.map((s, j) => (j === i ? { ...s, playing: false, progress: 0 } : s))
          );
        }
      } else if (a.src) {
        a.pause();
        a.removeAttribute('src');
        a.load();
        setSoundWeekState((prev) =>
          prev.map((s, j) => (j === i ? { ...s, playing: false, progress: s.progress } : s))
        );
      }
      const onTime = () => {
        const d = a.duration;
        if (!Number.isFinite(d) || d <= 0) return;
        const p = (a.currentTime / d) * 100;
        setSoundWeekState((prev) => {
          if (!prev[i]) return prev;
          return prev.map((s, j) =>
            j === i ? { ...s, progress: Math.min(100, Math.max(0, p)) } : s
          );
        });
      };
      const onMeta = () => {
        if (Number.isFinite(a.duration) && a.duration > 0) {
          setWeekMediaDuration((prev) => {
            const next = relaxationWeeks.map((_, j) => prev[j] ?? 0);
            next[i] = a.duration;
            return next;
          });
          const seg = listenSegRef.current.get(i);
          if (seg) {
            seg.durationCap = a.duration;
          }
        }
      };
      const onPlayingListen = () => {
        if (listenSegRef.current.has(i)) return;
        if (!w.cohortRelaxationWeeksId || !w.audioUrl) return;
        const cap = Number.isFinite(a.duration) && a.duration > 0 ? a.duration : null;
        listenSegRef.current.set(i, {
          t0: Date.now(),
          cohortRelaxationWeeksId: w.cohortRelaxationWeeksId,
          durationCap: cap,
        });
      };
      const onPauseListen = () => {
        flushRelaxListenSegment(i, a.ended ? 'ended' : 'pause', {});
      };
      const onEnded = () => {
        setSoundWeekState((prev) =>
          prev.map((s, j) => (j === i ? { ...s, playing: false, progress: 100 } : s))
        );
      };
      a.addEventListener('timeupdate', onTime);
      a.addEventListener('loadedmetadata', onMeta);
      a.addEventListener('ended', onEnded);
      a.addEventListener('playing', onPlayingListen);
      a.addEventListener('pause', onPauseListen);
      cleanups.push(() => {
        a.removeEventListener('timeupdate', onTime);
        a.removeEventListener('loadedmetadata', onMeta);
        a.removeEventListener('ended', onEnded);
        a.removeEventListener('playing', onPlayingListen);
        a.removeEventListener('pause', onPauseListen);
      });
    });
    return () => {
      const klist = [...listenSegRef.current.keys()];
      for (const k of klist) {
        flushRelaxListenSegment(k, 'context_change', {});
      }
      cleanups.forEach((f) => f());
    };
  }, [relaxationWeeks, flushRelaxListenSegment]);

  useEffect(() => {
    const onPageHide = () => {
      const klist = [...listenSegRef.current.keys()];
      for (const k of klist) {
        flushRelaxListenSegment(k, 'pagehide', { keepalive: true });
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [flushRelaxListenSegment]);

  useEffect(
    () => () => {
      soundWeekAudioMapRef.current.forEach((audio) => {
        try {
          audio.pause();
        } catch {
          /* no-op */
        }
      });
    },
    []
  );

  const missionStartForWeeks = useMemo(() => {
    const raw = selectedCohortItem?.missionStartAt;
    if (!raw) return startOfDay(MISSION_START);
    const d = startOfDay(new Date(raw));
    return Number.isNaN(d.getTime()) ? startOfDay(MISSION_START) : d;
  }, [selectedCohortItem]);
  const unlockedWeekCount = useMemo(() => {
    const today = startOfDay(new Date());
    const diffDays = Math.floor((today.getTime() - missionStartForWeeks.getTime()) / DAY_MS);
    if (diffDays < 0) return 0;
    return Math.min(4, Math.floor(diffDays / 7) + 1);
  }, [missionStartForWeeks]);

  /** 코호트 end_at 기준, 그 다음 달력 날(자정)부터 변화 리포트(검사하기) 열림 */
  const changeReportFirstOpenAt = useMemo(() => {
    const raw = selectedCohortItem?.missionEndAt;
    if (!raw) return null;
    const endDay = startOfDay(new Date(raw));
    if (Number.isNaN(endDay.getTime())) return null;
    const firstOpen = new Date(endDay);
    firstOpen.setDate(firstOpen.getDate() + 1);
    return firstOpen;
  }, [selectedCohortItem]);

  const isChangeReportUnlocked = useMemo(() => {
    if (!changeReportFirstOpenAt) return false;
    const today = startOfDay(new Date());
    return today.getTime() >= changeReportFirstOpenAt.getTime();
  }, [changeReportFirstOpenAt]);

  const changeReportDaysRemaining = useMemo(() => {
    if (!changeReportFirstOpenAt) return null;
    const today = startOfDay(new Date());
    const diffDays = Math.ceil((changeReportFirstOpenAt.getTime() - today.getTime()) / DAY_MS);
    return Math.max(0, diffDays);
  }, [changeReportFirstOpenAt]);
  const changeReportOpenDateLabel = useMemo(() => {
    if (!changeReportFirstOpenAt) return '0000년 00월 00일';
    const y = changeReportFirstOpenAt.getFullYear();
    const m = String(changeReportFirstOpenAt.getMonth() + 1).padStart(2, '0');
    const d = String(changeReportFirstOpenAt.getDate()).padStart(2, '0');
    return `${y}년 ${m}월 ${d}일`;
  }, [changeReportFirstOpenAt]);

  useEffect(() => {
    setSoundWeekOpen((prev) => {
      const next = [false, false, false, false];
      if (unlockedWeekCount > 0) {
        next[unlockedWeekCount - 1] = true;
      }
      if (prev.every((v, i) => v === next[i])) return prev;
      return next;
    });
  }, [unlockedWeekCount]);

  const loadChatMessages = useCallback(async () => {
    if (!selectedCohortCareOptionId || !currentUserId) {
      setChatMessages([]);
      setChatLoading(false);
      return;
    }
    setChatLoading(true);
    try {
    const { data: rows, error } = await supabase
      .from('group_chat_messages')
      .select('id, author_user_id, cohort_care_option_id, parent_id, body, created_at')
      .eq('cohort_care_option_id', selectedCohortCareOptionId)
      .order('created_at', { ascending: true });
    if (error) {
      console.error('[dashboard group_chat_messages]', error);
      setChatMessages([]);
      return;
    }

    const rawRows = rows || [];
    const rowsForDisplay = isSelfCohortSelected
      ? (() => {
          const myMessageIds = new Set(
            rawRows.filter((r) => r.author_user_id === currentUserId).map((r) => r.id)
          );
          return rawRows.filter(
            (r) =>
              r.author_user_id === currentUserId ||
              (r.parent_id != null && myMessageIds.has(r.parent_id))
          );
        })()
      : rawRows;

    const authorIds = [...new Set(rowsForDisplay.map((r) => r.author_user_id).filter(Boolean))];
    let profileMap = new Map();
    if (authorIds.length) {
      const { data: pRows, error: pErr } = await supabase
        .from('profiles')
        .select('id, nickname, avatar_url')
        .in('id', authorIds);
      if (pErr) {
        console.error('[dashboard group_chat_messages profiles]', pErr);
      } else {
        profileMap = new Map((pRows || []).map((p) => [p.id, p]));
      }
    }

    const baseMessages = rowsForDisplay.map((row) => {
      const p = profileMap.get(row.author_user_id);
      const rawName = p?.nickname != null ? String(p.nickname).trim() : '';
      return {
        id: row.id,
        sentAt: row.created_at,
        author: {
          name: rawName || '솜니아',
          avatar_url: p?.avatar_url || defaultGroupMemberAvatarUrl(row.author_user_id),
          role: STAFF_ROLE_BY_USER_ID.get(row.author_user_id),
          isSelf: row.author_user_id === currentUserId,
        },
        body: row.body || '',
        parentId: row.parent_id || null,
      };
    });

    const byId = new Map(baseMessages.map((m) => [m.id, m]));
    const merged = baseMessages.map((m) => {
      if (!m.parentId) return m;
      const parent = byId.get(m.parentId);
      if (!parent) return m;
      return {
        ...m,
        replyTo: {
          id: parent.id,
          authorName: parent.author.name,
          preview: truncateChatPreview(parent.body),
        },
      };
    });
    setChatMessages(merged);
    } finally {
      setChatLoading(false);
    }
  }, [selectedCohortCareOptionId, currentUserId, isSelfCohortSelected]);

  useEffect(() => {
    if (enrollmentState !== 'allowed') {
      return;
    }
    if (!selectedCohortCareOptionId) {
      setGroupMembersList([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const list = await buildGroupMembersFromCohortOption(supabase, selectedCohortCareOptionId);
      if (cancelled) return;
      setGroupMembersList(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [enrollmentState, selectedCohortCareOptionId]);

  const COACH_USER_ID = '391ac297-a111-42cd-84cc-91e4eb02e478';

  const visibleGroupMembers = useMemo(() => {
    if (!isSelfCohortSelected || !currentUserId) return groupMembersList;
    return groupMembersList.filter((m) => m.id === currentUserId || m.id === COACH_USER_ID);
  }, [groupMembersList, isSelfCohortSelected, currentUserId]);

  /** AI 슬립코치 typing 인디케이터에 사용할 코치 프로필 이미지·이름 */
  const coachProfile = useMemo(() => {
    const m = groupMembersList.find((x) => x.id === COACH_USER_ID);
    return {
      name: m?.name || 'AI 슬립코치',
      avatar_url: m?.avatar_url || defaultGroupMemberAvatarUrl(COACH_USER_ID),
    };
  }, [groupMembersList]);

  useEffect(() => {
    if (tab !== 'meet') return;
    loadChatMessages();
  }, [tab, loadChatMessages]);

  useEffect(() => {
    if (tab !== 'meet') return undefined;
    if (!selectedCohortCareOptionId) return undefined;
    const channel = supabase
      .channel(`group-chat-${selectedCohortCareOptionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'group_chat_messages',
          filter: `cohort_care_option_id=eq.${selectedCohortCareOptionId}`,
        },
        () => {
          loadChatMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tab, selectedCohortCareOptionId, loadChatMessages]);

  const headerCohortDisplay = useMemo(() => {
    if (selectedCohortItem) {
      return {
        roundLabel: selectedCohortItem.roundLabel,
        careTitle: selectedCohortItem.careTitle,
        dateLine: selectedCohortItem.dateLine,
      };
    }
    return {
      roundLabel: MOCK_COHORT_OPTIONS[0].roundLabel,
      careTitle: MOCK_COHORT_OPTIONS[0].careTitle,
      dateLine: defaultMissionDateLine(),
    };
  }, [selectedCohortItem]);

  const clearProfileError = useCallback((key) => {
    setProfileErrors((prev) => (prev[key] ? { ...prev, [key]: null } : prev));
  }, []);

  const profileFormComplete = useMemo(
    () => isProfileFormComplete(nickname, gender, birth, phone, fourWeekChangeGoal),
    [nickname, gender, birth, phone, fourWeekChangeGoal]
  );
  const canUseCareTabs = profileFormComplete && isPreSurveyCompleted;
  const showProfileGate = !canUseCareTabs && (tab === 'home' || tab === 'meet');

  useEffect(() => {
    if (isPreSurveyCompletionLoading || isProfileStatusLoading) return;
    if (hasAppliedInitialGateTab) return;
    if (!canUseCareTabs) {
      setTab('my');
    }
    setHasAppliedInitialGateTab(true);
  }, [
    isPreSurveyCompletionLoading,
    isProfileStatusLoading,
    canUseCareTabs,
    hasAppliedInitialGateTab,
  ]);

  const handleProfileSave = useCallback(async () => {
    const next = {
      nickname: null,
      gender: null,
      birth: null,
      phone: null,
      fourWeekGoal: null,
    };
    if (!nickname?.trim()) next.nickname = '닉네임을 입력해 주세요.';
    if (!gender) next.gender = '성별을 선택해 주세요.';
    if (!birth?.trim()) next.birth = '생년월일을 선택해 주세요.';
    if (!phoneOnlyDigits(phone)) next.phone = '휴대폰 번호를 입력해 주세요.';
    if (!fourWeekChangeGoal?.trim()) next.fourWeekGoal = '4주 변화 목표를 입력해 주세요.';

    setProfileErrors(next);
    if (next.nickname || next.gender || next.birth || next.phone || next.fourWeekGoal) return;

    setProfileSaveBusy(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        window.alert('로그인이 필요합니다.');
        return;
      }

      const payload = {
        nickname: nickname.trim(),
        gender,
        birth: birth || null,
        phone: phoneFormattedForDb(phone),
        goal: fourWeekChangeGoal.trim(),
      };

      const userId = session.user.id;
      const debugTag = '[dashboard profile save]';

      const { data: updatedRows, error: updateErr } = await supabase
        .from('profiles')
        .update(payload)
        .eq('id', userId)
        .select('id');

      if (updateErr) {
        console.error(debugTag, 'update error (full object)', updateErr);
        console.error(debugTag, 'update error (fields)', {
          message: updateErr?.message,
          code: updateErr?.code,
          details: updateErr?.details,
          hint: updateErr?.hint,
        });
        window.alert('프로필 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      if (!updatedRows?.length) {
        console.log(debugTag, 'no row updated, attempting insert (profiles row may be missing)', {
          userId,
        });
        const insertBody = {
          id: userId,
          ...payload,
          avatar_url: profileAvatarUrl,
        };
        console.log(debugTag, 'insert body', insertBody);
        const { data: insertData, error: insertErr } = await supabase
          .from('profiles')
          .insert(insertBody)
          .select('id');
        console.log(debugTag, 'insert response', { insertData, hasError: !!insertErr });
        if (insertErr) {
          console.error(debugTag, 'insert error (full object)', insertErr);
          console.error(debugTag, 'insert error (fields)', {
            message: insertErr?.message,
            code: insertErr?.code,
            details: insertErr?.details,
            hint: insertErr?.hint,
          });
          window.alert('프로필 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
          return;
        }
        console.log(debugTag, 'insert ok', insertData);
      } else {
        console.log(debugTag, 'update ok (rows)', updatedRows);
      }

      window.alert('저장되었습니다.');
    } catch (e) {
      console.error('[dashboard profile save] catch (unexpected)', e);
      if (e && typeof e === 'object' && 'stack' in e) {
        console.error('[dashboard profile save] catch (stack)', e.stack);
      }
      window.alert('오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setProfileSaveBusy(false);
    }
  }, [nickname, gender, birth, phone, fourWeekChangeGoal, profileAvatarUrl]);

  const missionRange = useMemo(() => {
    const rawStart = selectedCohortItem?.missionStartAt;
    const rawEnd = selectedCohortItem?.missionEndAt;
    const s = rawStart ? startOfDay(new Date(rawStart)) : fallbackMissionStart;
    const e = rawEnd ? startOfDay(new Date(rawEnd)) : fallbackMissionEnd;
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || s > e) {
      return { start: fallbackMissionStart, end: fallbackMissionEnd };
    }
    return { start: s, end: e };
  }, [selectedCohortItem, fallbackMissionStart, fallbackMissionEnd]);

  const missionCalendarCells = useMemo(
    () => buildMissionCalendarCells(missionRange.start, missionRange.end),
    [missionRange]
  );
  const todayMissionDateKey = useMemo(() => toDateKey(new Date()), []);

  const weekCompleteFlags = useMemo(() => {
    return [0, 1, 2, 3].map((weekIndex) => {
      for (let d = 0; d < 7; d += 1) {
        const key = missionDateKeyByWeekDayIndex(missionRange.start, weekIndex, d);
        if (!missionCompleteByDate[key]) return false;
      }
      return true;
    });
  }, [missionCompleteByDate, missionRange]);

  useEffect(() => {
    const selected = parseDateKey(selectedMissionDateKey);
    const s = missionRange.start;
    const e = missionRange.end;
    const inRange = selected >= s && selected <= e;
    if (inRange) return;
    setSelectedMissionDateKey(defaultSelectedMissionDateKey(s, e));
  }, [missionRange, selectedMissionDateKey]);

  /* ── 날짜 변경 시 녹음 상태 초기화 + 스트림 정리 ── */
  useEffect(() => {
    stopRecordingStream();
    setRecState('idle');
    setAudioBlob(null);
    setAudioR2Key(null);
    setIsTranscribing(false);
    setTranscribeError('');
    setRecSeconds(0);
    missionTranscribeAttemptKeyRef.current = null;
    setMissionTextEditingByDate({});
  }, [selectedMissionDateKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 마운트: 슬립케어 신청(checkouts) 여부 → 동의·제출 내역 로드 ── */
  useEffect(() => {
    (async () => {
      setIsProfileStatusLoading(true);
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        setVoiceConsent(false);
        setEnrollmentState('blocked');
        setIsProfileStatusLoading(false);
        return;
      }
      setCurrentUserId(session.user.id);

      const { data: checkoutRows, error: checkoutError } = await supabase
        .from('checkouts')
        .select('id')
        .eq('auth_user_id', session.user.id)
        .limit(1);

      if (checkoutError) {
        console.error('[dashboard checkouts]', checkoutError);
        setVoiceConsent(false);
        setEnrollmentState('blocked');
        setIsProfileStatusLoading(false);
        return;
      }

      if (!checkoutRows?.length) {
        setVoiceConsent(false);
        setEnrollmentState('blocked');
        setIsProfileStatusLoading(false);
        return;
      }

      const { data: checkoutCohortList, error: chCoErr } = await supabase
        .from('checkouts')
        .select(
          'id, created_at, cohort_care_option_id, payment_status, refund_status, refund_amount, payment_amount, cohort_care_options ( cohort_number, cohort_label, title, start_at, end_at )'
        )
        .eq('auth_user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (chCoErr) {
        console.error('[dashboard cohort_care_options]', chCoErr);
        const fallbackCohortId = COHORT_CARE_OPTION_IDS[0] || null;
        setCohortMenuItems([
          {
            id: fallbackCohortId || 'fallback-cohort',
            roundLabel: '—',
            careTitle: '슬립케어',
            dateLine: defaultMissionDateLine(),
            cohortCareOptionId: fallbackCohortId,
            missionStartAt: MISSION_START,
            missionEndAt: MISSION_END,
          },
        ]);
        setSelectedCohortId(fallbackCohortId || 'fallback-cohort');
      } else {
        const list = checkoutCohortList || [];
        // 헤더 표시(기수/타이틀)는 결제 상태와 무관하게 본인 checkouts 의 cohort id 기준으로 구성
        const activeRows = list.filter((row) => Boolean(row?.cohort_care_option_id));
        const cohortIdsForHydrate = [
          ...new Set(activeRows.map((row) => row.cohort_care_option_id).filter(Boolean)),
        ];
        let hydratedOptionMap = new Map();
        if (cohortIdsForHydrate.length) {
          const { data: optionRows, error: optionErr } = await supabase
            .from('cohort_care_options')
            .select('id, cohort_number, cohort_label, title, start_at, end_at')
            .in('id', cohortIdsForHydrate);
          if (optionErr) {
            console.error('[dashboard cohort label hydrate]', optionErr);
          } else {
            hydratedOptionMap = new Map((optionRows || []).map((r) => [r.id, r]));
          }
        }
        const seenCohortOption = new Set();
        const items = [];
        for (const row of activeRows) {
          const co = row.cohort_care_options;
          const c = co ? (Array.isArray(co) ? co[0] : co) : null;
          const ck = row.cohort_care_option_id ?? `row-${row.id}`;
          if (seenCohortOption.has(ck)) continue;
          /** join 실패·RLS 등으로 cohort_care_options 가 비어 있어도 checkouts.cohort_care_option_id 가 있으면 항목에 넣어야
           *  그룹챗 멤버 조회(cohortCareOptionId)가 목업(null)로 떨어지지 않음 */
          let labels;
          let missionWindow = null;
          if (c && (c.cohort_number != null || c.title != null)) {
            labels = cohortOptionToHeaderLabels(c);
            missionWindow = c;
          } else if (
            row.cohort_care_option_id &&
            hydratedOptionMap.has(row.cohort_care_option_id)
          ) {
            missionWindow = hydratedOptionMap.get(row.cohort_care_option_id);
            labels = cohortOptionToHeaderLabels(missionWindow);
          } else if (row.cohort_care_option_id) {
            labels = {
              roundLabel: '—',
              careTitle: '슬립케어',
              dateLine: defaultMissionDateLine(),
            };
          } else {
            continue;
          }
          if (!labels) continue;
          seenCohortOption.add(ck);
          items.push({
            id: row.cohort_care_option_id || `opt-${ck}`,
            cohortCareOptionId: row.cohort_care_option_id || null,
            cohortLabel: missionWindow?.cohort_label || null,
            missionStartAt: missionWindow?.start_at || null,
            missionEndAt: missionWindow?.end_at || null,
            ...labels,
          });
        }
        if (items.length) {
          setCohortMenuItems(items);
          setSelectedCohortId(items[0].id);
        } else {
          const fallbackCohortId = COHORT_CARE_OPTION_IDS[0] || null;
          setCohortMenuItems([
            {
              id: fallbackCohortId || 'fallback-cohort',
              roundLabel: '—',
              careTitle: '슬립케어',
              dateLine: defaultMissionDateLine(),
              cohortCareOptionId: fallbackCohortId,
              missionStartAt: MISSION_START,
              missionEndAt: MISSION_END,
            },
          ]);
          setSelectedCohortId(fallbackCohortId || 'fallback-cohort');
        }
      }

      // 동의 여부 + 프로필 필드 (표시·저장)
      const { data: profile } = await supabase
        .from('profiles')
        .select('voice_consent_at, avatar_url, nickname, gender, birth, phone, goal')
        .eq('id', session.user.id)
        .maybeSingle();
      setVoiceConsent(!!profile?.voice_consent_at);
      setProfileAvatarUrl(profile?.avatar_url || session.user.user_metadata?.avatar_url || null);

      let nextNick;
      if (profile) {
        nextNick = profile.nickname != null ? String(profile.nickname).trim() : '';
        setNickname(nextNick);
      } else {
        const metaName = session.user.user_metadata?.full_name;
        nextNick = (metaName && String(metaName).trim()) || '솜니아 닉네임';
        setNickname(nextNick);
      }
      const nextGender =
        profile?.gender && ['f', 'm', 'x'].includes(profile.gender) ? profile.gender : '';
      setGender(nextGender);
      const nextBirth = profileBirthToDateInputValue(profile?.birth);
      setBirth(nextBirth);
      const nextPhone = normalizeKoreanPhoneDigits(
        profile?.phone != null ? String(profile.phone) : ''
      );
      setPhone(nextPhone);
      const nextGoal = profile?.goal != null ? String(profile.goal) : '';
      setFourWeekChangeGoal(nextGoal);

      setEnrollmentState('allowed');
      setIsProfileStatusLoading(false);

      // 제출 내역 로드
      const { data } = await supabase
        .from('mission_submissions')
        .select('date_key, transcript, audio_r2_key')
        .eq('user_id', session.user.id);
      if (!data || data.length === 0) return;
      const completeFlags = {};
      const textMap = {};
      const recordedFlags = {};
      const unlockedFlags = {};
      data.forEach((row) => {
        completeFlags[row.date_key] = true;
        textMap[row.date_key] = row.transcript;
        recordedFlags[row.date_key] = true;
        unlockedFlags[row.date_key] = true;
      });
      setMissionCompleteByDate(completeFlags);
      setMissionTextByDate(textMap);
      setMissionRecordedByDate(recordedFlags);
      setMissionTextUnlockedByDate(unlockedFlags);
    })();
  }, []);

  /** 녹음 스트림 정리 */
  function stopRecordingStream() {
    if (recTimerRef.current) {
      clearInterval(recTimerRef.current);
      recTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    mediaRecorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  /** 동의서 제출 → Supabase 저장 */
  async function handleConsentAgree() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        alert('로그인이 필요합니다.');
        return;
      }
      const { error } = await supabase
        .from('profiles')
        .update({
          voice_consent_at: new Date().toISOString(),
          voice_consent_ua: navigator.userAgent,
        })
        .eq('id', session.user.id);
      if (error) {
        console.error('[voice_consent]', error);
        alert('동의 저장에 실패했습니다. 다시 시도해 주세요.');
        return;
      }
      setVoiceConsent(true);
      setConsentModalOpen(false);
      setConsentChecked(false);
    } catch (e) {
      console.error(e);
      alert('오류가 발생했습니다.');
    }
  }

  /** 녹음 시작 */
  async function startRecording() {
    setTranscribeError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunksRef.current = [];

      const mimeType = getSupportedMimeType();
      const options = mimeType ? { mimeType } : undefined;
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const mime = recorder.mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: mime });
        setAudioBlob(blob);
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };

      recorder.start(1000);
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => {
        setRecSeconds((prev) => {
          const next = prev + 1;
          if (next >= MAX_RECORDING_SECONDS) {
            stopRecording();
          }
          return next;
        });
      }, 1000);

      setRecState('recording');
    } catch (err) {
      console.error('[getUserMedia]', err);
      if (err.name === 'NotAllowedError') {
        setTranscribeError('마이크 권한을 허용해 주세요.');
      } else {
        setTranscribeError('마이크를 사용할 수 없습니다. HTTPS 환경인지 확인해 주세요.');
      }
    }
  }

  /** 녹음 중지 */
  function stopRecording() {
    if (recTimerRef.current) {
      clearInterval(recTimerRef.current);
      recTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecState('paused');
  }

  /** STT 변환 요청 (Step 3) */
  async function handleTranscribe() {
    if (!audioBlob) return;
    setIsTranscribing(true);
    setTranscribeError('');
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setTranscribeError('로그인이 필요합니다.');
        setIsTranscribing(false);
        return;
      }
      const result = await transcribeAudio(audioBlob, selectedMissionDateKey, session.access_token);
      if (result.success) {
        setAudioR2Key(result.r2Key);
        if (!result.transcript) {
          setTranscribeError(result.message || '음성이 인식되지 않았습니다. 다시 녹음해 주세요.');
          setRecState('paused');
        } else {
          setMissionTextByDate((prev) => ({
            ...prev,
            [selectedMissionDateKey]: result.transcript,
          }));
          setMissionTextUnlockedByDate((prev) => ({ ...prev, [selectedMissionDateKey]: true }));
          setRecState('textReady');
        }
      } else {
        setTranscribeError(result.message || '음성 인식에 실패했습니다.');
      }
    } catch (err) {
      setTranscribeError(err.message || '음성 변환 중 오류가 발생했습니다.');
    } finally {
      setIsTranscribing(false);
    }
  }

  useEffect(() => {
    if (recState !== 'paused' || !audioBlob) return;
    if (isTranscribing) return;
    const key = `${selectedMissionDateKey}::${audioBlob.size}::${audioBlob.type || ''}`;
    if (missionTranscribeAttemptKeyRef.current === key) return;
    missionTranscribeAttemptKeyRef.current = key;
    void handleTranscribe();
    // handleTranscribe는 위에서 정의되며 date_key·audioBlob·세션에 의존
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recState, audioBlob, isTranscribing, selectedMissionDateKey]);

  /* ── unmount 시 스트림 정리 ── */
  useEffect(() => {
    return () => stopRecordingStream();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await supabase.auth.signOut();
    sessionStorage.removeItem('mock_user');
    navigate('/');
  };

  const selectedMissionText = missionTextByDate[selectedMissionDateKey] ?? '';
  const isSelectedMissionComplete = Boolean(missionCompleteByDate[selectedMissionDateKey]);
  /** 게이트 우회 사용자 — 녹음/STT 결과 무관하게 텍스트 수정·공유 허용 */
  const isMissionFreeEditUser = !!currentUserId && MISSION_FREE_EDIT_USER_IDS.has(currentUserId);
  const hasSelectedRecording =
    Boolean(missionRecordedByDate[selectedMissionDateKey]) || isMissionFreeEditUser;
  const isMissionTextUnlocked =
    Boolean(missionTextUnlockedByDate[selectedMissionDateKey]) || isMissionFreeEditUser;
  const isMissionTextEditing = Boolean(missionTextEditingByDate[selectedMissionDateKey]);
  const isMissionTextEditable =
    isMissionTextUnlocked && !isSelectedMissionComplete && isMissionTextEditing;
  const isSelectedMissionFutureDay = selectedMissionDateKey > todayMissionDateKey;

  const missionFeedbackCopy = useMemo(() => {
    const shortForm = /한\s*마디|한마디/i.test(String(selectedCohortItem?.careTitle || ''));
    if (shortForm) {
      return {
        recordTitle: '오늘의 수면, 한마디로 남겨볼까요?',
        textTitleSuffix: '한 마디',
        beforeRecBody: '오늘 이완 훈련을 하며 느낀 점을 60초 안에 말로 남겨보세요.',
        whileRecordingBody: '‘종료’를 누르면 AI가 자동으로 텍스트로 변환해줘요.',
        transcribingBody:
          'AI가 음성을 텍스트로 옮기고 있어요. 아래 박스에서 변환 진행을 확인해 주세요.',
        afterRecBody: '녹음을 처음부터 다시 하고 싶다면 ‘다시 녹음’을 누르세요.',
        afterShareBody: '오늘의 셀프 피드백이 잘 기록되었어요.',
        textBoxHintBefore: isSelfCohortSelected
          ? '녹음을 완료하고, 텍스트로 변환된 내용을 편하게 수정하세요.'
          : '‘공유’를 눌러 오늘의 느낀 점을 그룹챗에 함께 나눠보세요.',
      };
    }
    return {
      recordTitle: isSelfCohortSelected ? '오늘 하루는 어땠나요?' : '오늘 이완 훈련은 어땠나요?',
      textTitleSuffix: isSelfCohortSelected ? '한마디' : '셀프 피드백',
      beforeRecBody: '오늘 이완 훈련을 하며 느낀 점을 자유롭게 녹음으로 남겨보세요(60초).',
      whileRecordingBody: '‘종료’를 누르면 AI가 자동으로 텍스트로 변환해줘요.',
      transcribingBody:
        'AI가 음성을 텍스트로 옮기고 있어요. 아래 박스에서 변환 진행을 확인해 주세요.',
      afterRecBody: '녹음을 처음부터 다시 하고 싶다면 ‘다시 녹음’을 누르세요.',
      afterShareBody: '오늘의 셀프 피드백이 잘 기록되었어요.',
      textBoxHintBefore: isSelfCohortSelected
        ? '녹음을 완료하고, 텍스트로 변환된 내용을 편하게 수정하세요.'
        : '‘공유’를 눌러 오늘의 느낀 점을 그룹챗에 함께 나눠보세요.',
    };
  }, [selectedCohortItem, isSelfCohortSelected]);

  const missionRecordSectionDesc = useMemo(() => {
    if (isSelectedMissionComplete) {
      return missionFeedbackCopy.afterShareBody;
    }
    if (isTranscribing) {
      return missionFeedbackCopy.transcribingBody;
    }
    if (
      recState === 'paused' &&
      hasSelectedRecording &&
      !isMissionTextUnlocked &&
      !transcribeError
    ) {
      return missionFeedbackCopy.transcribingBody;
    }
    if (recState === 'textReady' && hasSelectedRecording && isMissionTextUnlocked) {
      return missionFeedbackCopy.afterRecBody;
    }
    if (recState === 'recording') {
      return missionFeedbackCopy.whileRecordingBody;
    }
    return missionFeedbackCopy.beforeRecBody;
  }, [
    isSelectedMissionComplete,
    isTranscribing,
    recState,
    hasSelectedRecording,
    isMissionTextUnlocked,
    transcribeError,
    missionFeedbackCopy,
  ]);

  const missionTextSectionTitle = `${formatMissionDateLabel(
    selectedMissionDateKey
  )} · 오늘의 ${missionFeedbackCopy.textTitleSuffix}`;

  /** 녹음 중이거나 변환 중이면 다른 인터랙션 잠금 */
  const isMissionBusy = recState === 'recording' || isTranscribing;

  const setSelectedMissionText = (value) => {
    setMissionTextByDate((prev) => ({ ...prev, [selectedMissionDateKey]: value }));
  };

  const handleMissionCalendarPick = (dateKey, inMission) => {
    if (!inMission || isMissionBusy) return;
    setSelectedMissionDateKey(dateKey);
  };

  /** 제출 버튼 클릭 → 모달 열기 */
  const handleMissionSubmitClick = () => {
    if (isSelectedMissionComplete || isMissionTextEditing) return;
    const text = selectedMissionText.trim();
    if (!text || !hasSelectedRecording || !isMissionTextUnlocked) return;
    setConfirmModalOpen(true);
  };

  const toggleMissionTextEditing = () => {
    if (!isMissionTextUnlocked || isSelectedMissionComplete) return;
    setMissionTextEditingByDate((prev) => ({
      ...prev,
      [selectedMissionDateKey]: !prev[selectedMissionDateKey],
    }));
  };

  /** 안내 모달 닫기 → AI 슬립코치 탭으로 이동 */
  const handleCoachRedirectClose = useCallback(() => {
    setCoachRedirectModalOpen(false);
    setTab('meet');
  }, []);

  /** 안내 모달이 열리면 4초 후 자동으로 닫고 탭 이동 */
  useEffect(() => {
    if (!coachRedirectModalOpen) return undefined;
    const timer = setTimeout(() => {
      handleCoachRedirectClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [coachRedirectModalOpen, handleCoachRedirectClose]);

  /** SELF 코호트 전용 — 서버에 코치 응답 요청 (실패 시 재시도 토스트로 노출) */
  const triggerCoachReply = useCallback(
    async ({ userText, dateKey, cohortCareOptionId, parentMessageId, accessToken }) => {
      lastCoachRequestRef.current = {
        userText,
        dateKey,
        cohortCareOptionId,
        parentMessageId,
        accessToken,
      };
      setCoachReplyError(null);
      setCoachReplyPending(true);
      try {
        await requestCoachReply({
          userText,
          dateKey,
          cohortCareOptionId,
          parentMessageId,
          accessToken,
        });
        await loadChatMessages();
      } catch (err) {
        console.error('[coach reply]', err);
        setCoachReplyError(err?.message || 'AI 슬립코치 응답에 실패했습니다.');
      } finally {
        setCoachReplyPending(false);
      }
    },
    [loadChatMessages]
  );

  const retryCoachReply = useCallback(async () => {
    const last = lastCoachRequestRef.current;
    if (!last) return;
    let token = last.accessToken;
    try {
      const { data } = await supabase.auth.getSession();
      token = data?.session?.access_token || token;
    } catch {
      // ignore — fall back to stored token
    }
    await triggerCoachReply({ ...last, accessToken: token });
  }, [triggerCoachReply]);

  /** 모달에서 "공유" 확인 → mission_submissions + 그룹챗 + (SELF면) AI 슬립코치 호출 */
  const handleMissionSubmitConfirm = async () => {
    setConfirmModalOpen(false);
    const text = selectedMissionText.trim();
    if (!text) return;

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        alert('로그인이 필요합니다.');
        return;
      }
      const { error } = await supabase.from('mission_submissions').insert({
        user_id: session.user.id,
        date_key: selectedMissionDateKey,
        audio_r2_key: audioR2Key || null,
        transcript: text,
        cohort_care_options_id: selectedCohortCareOptionId || null,
      });
      if (error) {
        if (error.code === '23505') {
          alert('이미 제출된 날짜입니다.');
          setMissionCompleteByDate((prev) => ({ ...prev, [selectedMissionDateKey]: true }));
          return;
        }
        console.error('[mission submit]', error);
        alert('제출에 실패했습니다. 다시 시도해 주세요.');
        return;
      }
      if (selectedCohortCareOptionId && currentUserId) {
        const dateLabel = formatMissionDateLabel(selectedMissionDateKey);
        const sharedBody = `[셀프 피드백 · ${dateLabel}]\n${text}`;
        const { data: insertedShare, error: chatErr } = await supabase
          .from('group_chat_messages')
          .insert({
            cohort_care_option_id: selectedCohortCareOptionId,
            author_user_id: currentUserId,
            parent_id: null,
            body: sharedBody,
          })
          .select('id')
          .single();
        if (chatErr) {
          console.error('[mission share chat]', chatErr);
          alert('기록은 저장되었으나 그룹챗에 올리지 못했습니다. 잠시 후 다시 시도해 주세요.');
        } else {
          await loadChatMessages();
          if (isSelfCohortSelected) {
            /** SELF: 안내 모달을 먼저 띄우고, Dify 호출은 백그라운드 시작.
             *  모달의 "확인" 버튼 또는 자동 닫힘 시점에 setTab('meet') 수행 */
            setCoachRedirectModalOpen(true);
            void triggerCoachReply({
              userText: sharedBody,
              dateKey: selectedMissionDateKey,
              cohortCareOptionId: selectedCohortCareOptionId,
              parentMessageId: insertedShare?.id || null,
              accessToken: session.access_token,
            });
          } else {
            setTab('meet');
          }
        }
      }
      setMissionTextEditingByDate((prev) => ({ ...prev, [selectedMissionDateKey]: false }));
      setMissionCompleteByDate((prev) => ({ ...prev, [selectedMissionDateKey]: true }));
    } catch (e) {
      console.error(e);
      alert('제출 중 오류가 발생했습니다.');
    }
  };

  useEffect(() => {
    if (tab !== 'meet') return;
    requestAnimationFrame(() => {
      chatScrollEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, [chatMessages.length, tab, coachReplyPending, coachReplyError]);

  const handleChatSend = useCallback(async () => {
    if (chatSending) return;
    // 기간 종료 후 AI 슬립코치(SELF)는 대화 불가 (그룹챗은 계속 가능)
    if (isCoachExpired) {
      window.alert('프로그램 기간이 종료되어 AI 슬립코치와 더 이상 대화할 수 없어요. 기간을 연장하면 다시 이용할 수 있어요.');
      return;
    }
    const text = chatInput.trim();
    if (!text) return;
    if (!selectedCohortCareOptionId || !currentUserId) return;
    setChatSending(true);
    try {
      const payload = {
        cohort_care_option_id: selectedCohortCareOptionId,
        author_user_id: currentUserId,
        parent_id: replyingTo?.id || null,
        body: text,
      };
      const { data: insertedMsg, error } = await supabase
        .from('group_chat_messages')
        .insert(payload)
        .select('id')
        .single();
      if (error) {
        console.error('[dashboard group_chat_messages insert]', error);
        alert('메시지 전송에 실패했습니다.');
        return;
      }
      setChatInput('');
      setReplyingTo(null);
      await loadChatMessages();

      /** SELF 코호트(AI 슬립코치)에서 보낸 메시지는 Dify로 응답 요청 */
      if (isSelfCohortSelected) {
        try {
          const { data } = await supabase.auth.getSession();
          const accessToken = data?.session?.access_token;
          if (accessToken) {
            void triggerCoachReply({
              userText: text,
              dateKey: todayMissionDateKey,
              cohortCareOptionId: selectedCohortCareOptionId,
              parentMessageId: insertedMsg?.id || null,
              accessToken,
            });
          }
        } catch (e) {
          console.error('[chat → coach reply trigger]', e);
        }
      }
    } finally {
      setChatSending(false);
    }
  }, [
    chatSending,
    chatInput,
    replyingTo,
    selectedCohortCareOptionId,
    currentUserId,
    loadChatMessages,
    isSelfCohortSelected,
    triggerCoachReply,
    todayMissionDateKey,
    isCoachExpired,
  ]);

  if (enrollmentState === 'loading') {
    return (
      <div className="db-root db-root--enrollment-gate" aria-busy="true">
        <div
          className="db-enrollment-gate db-enrollment-gate--loading"
          role="status"
          aria-live="polite"
          aria-label="불러오는 중"
        >
          <span className="db-enrollment-gate-indicator" aria-hidden />
        </div>
      </div>
    );
  }

  if (enrollmentState === 'blocked') {
    return (
      <div className="db-root db-root--enrollment-gate">
        <div className="db-enrollment-gate">
          <p className="db-enrollment-gate-text">신청한 내역이 없습니다.</p>
          <p className="db-enrollment-gate-hint">슬립케어를 신청·결제하신 뒤 다시 방문해 주세요.</p>
          <Link to="/" className="db-enrollment-gate-link">
            홈으로
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="db-root">
      <header className="db-header">
        <div className="db-header-inner">
          <Link to="/" className="db-logo" aria-label="홈으로 이동">
            Somnia Sleep Care
          </Link>
          <div className="db-cohort-wrap" ref={cohortWrapRef}>
            <button
              type="button"
              className="db-cohort-trigger"
              data-cohort-care-option-id={selectedCohortCareOptionId || undefined}
              aria-expanded={cohortMenuOpen}
              aria-haspopup="listbox"
              onClick={() => setCohortMenuOpen((o) => !o)}
            >
              <span className="db-cohort-trigger-text">
                <span className="db-cohort-line1">
                  <span className="db-cohort-round">{headerCohortDisplay.roundLabel}</span>
                  <span className="db-cohort-sep" aria-hidden>
                    {' '}
                  </span>
                  <span className="db-cohort-care">{headerCohortDisplay.careTitle}</span>
                </span>
                <span className="db-cohort-line2">{headerCohortDisplay.dateLine}</span>
              </span>
              <span
                className={`db-cohort-chevron ${cohortMenuOpen ? 'db-cohort-chevron--open' : ''}`}
                aria-hidden
              >
                ▼
              </span>
            </button>
            {cohortMenuOpen && cohortMenuItems.length > 0 && (
              <ul className="db-cohort-menu" role="listbox" aria-label="기수·케어 선택">
                {cohortMenuItems.map((c) => (
                  <li key={c.id} role="option" aria-selected={c.id === selectedCohortId}>
                    <button
                      type="button"
                      className={`db-cohort-option ${
                        c.id === selectedCohortId ? 'db-cohort-option--active' : ''
                      }`}
                      onClick={() => {
                        setSelectedCohortId(c.id);
                        setCohortMenuOpen(false);
                      }}
                    >
                      <span className="db-cohort-option-inner">
                        <span className="db-cohort-option-line1">
                          <span className="db-cohort-option-round">{c.roundLabel}</span>
                          <span className="db-cohort-option-care">{c.careTitle}</span>
                        </span>
                        {c.dateLine ? (
                          <span className="db-cohort-option-date">{c.dateLine}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </header>

      <div className={`db-column ${tab === 'meet' ? 'db-column--meet' : ''}`}>
        <div
          className={
            showProfileGate
              ? 'db-column-main-wrap db-column-main-wrap--gated'
              : 'db-column-main-wrap'
          }
        >
          <main
            className={`db-main ${tab === 'meet' ? 'db-main--meet' : ''}${
              showProfileGate ? ' db-main--profile-gate-blur' : ''
            }`}
            aria-hidden={showProfileGate}
          >
            {tab === 'home' && (
              <>
                <section className="db-card" aria-labelledby="db-sound-title">
                  <h2 id="db-sound-title" className="db-card-title">
                    이완 훈련
                  </h2>

                  {isProgramExpired && (
                    <p className="db-chat-expired-banner" role="note">
                      프로그램 기간이 종료되었어요. 기간을 연장하면 이완 콘텐츠를 다시 이용할 수 있어요.
                    </p>
                  )}

                  <div className="db-sound-weeks">
                    {relaxationWeeks.map((w, i) => {
                      const open = soundWeekOpen[i];
                      const st = soundWeekState[i];
                      const totalSeconds = resolveTotalSeconds(i, w);
                      const currentSeconds = Math.round((st.progress / 100) * (totalSeconds || 0));
                      const headId = `db-sound-week-head-${w.id}`;
                      const panelId = `db-sound-week-panel-${w.id}`;
                      const isWeekLocked = w.id > unlockedWeekCount;
                      return (
                        <div
                          key={w.id}
                          className={`db-sound-week ${isWeekLocked ? 'db-sound-week--locked' : ''}`}
                        >
                          <button
                            id={headId}
                            type="button"
                            className={`db-sound-week-head ${isWeekLocked ? 'db-sound-week-head--locked' : ''}`}
                            disabled={isWeekLocked}
                            onClick={() => {
                              if (!isWeekLocked) toggleSoundWeekOpen(i);
                            }}
                            aria-expanded={isWeekLocked ? false : open}
                            aria-controls={isWeekLocked ? undefined : panelId}
                            title={
                              isWeekLocked ? '이 주차 콘텐츠는 아직 열리지 않았어요' : undefined
                            }
                          >
                            <div className="db-sound-week-thumb-wrap">
                              <img
                                className="db-sound-week-thumb"
                                src={w.thumb}
                                alt=""
                                width={160}
                                height={90}
                                loading="lazy"
                              />
                              <span className="db-sound-week-thumb-duration" aria-hidden>
                                {w.durationLabel}
                              </span>
                              {isWeekLocked && (
                                <div className="db-sound-week-thumb-lock" aria-hidden>
                                  <svg
                                    className="db-sound-week-thumb-lock-icon"
                                    width="28"
                                    height="28"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M7 11V8a5 5 0 0110 0v3M6 11h12a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6a2 2 0 012-2z"
                                      stroke="currentColor"
                                      strokeWidth="1.75"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div className="db-sound-week-head-text">
                              <p className="db-sound-week-label">Week {w.id}.</p>
                              <div className="db-sound-week-title-row">
                                <h3 className="db-sound-week-title">{w.title}</h3>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  className="db-sound-week-info-btn"
                                  aria-label={`Week ${w.id} 안내 보기`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setWeekInfoModal({
                                      open: true,
                                      title: w.title,
                                      information: w.information || '안내 정보가 없습니다.',
                                    });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key !== 'Enter' && e.key !== ' ') return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setWeekInfoModal({
                                      open: true,
                                      title: w.title,
                                      information: w.information || '안내 정보가 없습니다.',
                                    });
                                  }}
                                >
                                  i
                                </span>
                              </div>
                              <p className="db-sound-week-desc">{w.description}</p>
                            </div>
                            {!isWeekLocked ? (
                              <span
                                className={`db-sound-week-chevron ${open ? 'db-sound-week-chevron--open' : ''}`}
                                aria-hidden
                              >
                                ▼
                              </span>
                            ) : (
                              <span
                                className="db-sound-week-chevron db-sound-week-chevron--muted"
                                aria-hidden
                              >
                                —
                              </span>
                            )}
                          </button>

                          <div
                            id={panelId}
                            role="region"
                            aria-labelledby={headId}
                            className="db-sound-week-panel"
                            hidden={isWeekLocked || !open}
                          >
                            <div className="db-player">
                              <div
                                className="db-player-controls"
                                role="group"
                                aria-label="재생 조작"
                              >
                                <button
                                  type="button"
                                  className="db-skip-btn"
                                  onClick={() => seekSoundWeekSeconds(i, -10)}
                                  disabled={totalSeconds <= 0 || !w.audioUrl}
                                  aria-label="10초 뒤로"
                                >
                                  <IconSoundSkipBack />
                                </button>
                                <button
                                  type="button"
                                  className="db-play-btn"
                                  onClick={() => toggleSoundWeekPlay(i)}
                                  disabled={!w.audioUrl || (isProgramExpired && !st.playing)}
                                  aria-label={st.playing ? '일시정지' : '재생'}
                                  title={
                                    isProgramExpired && !st.playing
                                      ? '프로그램 기간이 종료되어 재생할 수 없어요'
                                      : undefined
                                  }
                                >
                                  {st.playing ? '❚❚' : '▶'}
                                </button>
                                <button
                                  type="button"
                                  className="db-skip-btn"
                                  onClick={() => seekSoundWeekSeconds(i, 10)}
                                  disabled={totalSeconds <= 0 || !w.audioUrl}
                                  aria-label="10초 앞으로"
                                >
                                  <IconSoundSkipForward />
                                </button>
                              </div>
                              <div className="db-player-body">
                                <div className="db-row db-row--between db-row--tight db-player-time-row">
                                  <span className="db-muted db-player-time">
                                    {formatSecondsLabel(currentSeconds)} /{' '}
                                    {formatSecondsLabel(totalSeconds)}
                                  </span>
                                </div>
                                <div className="db-progress-wrap">
                                  <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={st.progress}
                                    onChange={(e) => {
                                      const next = Number(e.target.value);
                                      if (!Number.isFinite(next)) return;
                                      const clamped = Math.min(100, Math.max(0, next));
                                      if (w.audioUrl) {
                                        const a = soundWeekAudioMapRef.current.get(i);
                                        const t = resolveTotalSeconds(i, w);
                                        if (a && t > 0) {
                                          a.currentTime = (clamped / 100) * t;
                                        }
                                      }
                                      patchSoundWeek(i, { progress: clamped });
                                    }}
                                    className="db-range db-range--progress"
                                    aria-label={`Week ${w.id} 재생 위치`}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section className="db-card" aria-labelledby="db-mission-title">
                  <h2 id="db-mission-title" className="db-card-title">
                    {isSelfCohortSelected ? '음성 다이어리(60초)' : '셀프 피드백(60초)'}
                  </h2>

                  <div className="db-mission-flow">
                    <div className="db-mission-step">
                      <div className="db-mission-step-body">
                        <div className={`db-cal ${isMissionBusy ? 'db-mission-locked' : ''}`}>
                          {MISSION_WEEKDAY_HEADERS.map((d) => (
                            <span key={d} className="db-cal-dow">
                              {d}
                            </span>
                          ))}
                          {missionCalendarCells.map((cell) => {
                            const isToday = cell.dateKey === todayMissionDateKey;
                            const isFutureMissionDay =
                              cell.inMission && !isToday && cell.dateKey > todayMissionDateKey;
                            const isSelected = cell.dateKey === selectedMissionDateKey;
                            const isComplete = Boolean(missionCompleteByDate[cell.dateKey]);
                            const classNames = [
                              'db-cal-cell',
                              !cell.inMission && 'db-cal-cell--outside',
                              cell.inMission && isToday && 'db-cal-cell--today',
                              isFutureMissionDay && 'db-cal-cell--future',
                              cell.inMission && isSelected && 'db-cal-cell--selected',
                              cell.inMission && isComplete && 'db-cal-cell--complete',
                            ]
                              .filter(Boolean)
                              .join(' ');
                            return (
                              <button
                                key={cell.dateKey}
                                type="button"
                                className={classNames}
                                disabled={!cell.inMission}
                                onClick={() =>
                                  handleMissionCalendarPick(cell.dateKey, cell.inMission)
                                }
                                aria-label={`${formatMissionDateLabel(cell.dateKey)}${isToday ? ' 오늘' : ''}${isFutureMissionDay ? ' 예정된 날' : ''}${isComplete ? ' 제출 완료' : ''}`}
                                aria-pressed={cell.inMission && isSelected}
                              >
                                {cell.monthChange && (
                                  <span className="db-cal-cell-month">{cell.month}월</span>
                                )}
                                <span className="db-cal-cell-day">{cell.day}</span>
                                {cell.inMission && isComplete && (
                                  <span className="db-cal-cell-check" aria-hidden>
                                    ✓
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="db-mission-step">
                      <div className="db-mission-step-head">
                        <div className="db-mission-step-head-row">
                          <div className="db-mission-step-head-copy">
                            <div className="db-mission-step-title-with-icon">
                              <span className="db-mission-step-inline-icon" aria-hidden>
                                <IconMissionRecord size={20} />
                              </span>
                              <h3 className="db-mission-step-title">
                                {missionFeedbackCopy.recordTitle}
                                {recState === 'recording' && (
                                  <span className="db-rec-dot db-rec-dot--label" aria-hidden />
                                )}
                              </h3>
                            </div>
                            <p className="db-mission-step-desc db-mission-step-desc--with-icon">
                              <span>{missionRecordSectionDesc}</span>
                            </p>
                          </div>
                          <button
                            type="button"
                            className={`db-btn db-btn--primary db-mission-step-action ${recState === 'recording' ? 'db-btn--recording' : ''}`}
                            disabled={
                              isSelectedMissionComplete ||
                              isTranscribing ||
                              isSelectedMissionFutureDay
                            }
                            onClick={async () => {
                              if (isSelectedMissionComplete || isSelectedMissionFutureDay) return;
                              if (recState === 'idle') {
                                if (!voiceConsent) {
                                  setConsentModalOpen(true);
                                  return;
                                }
                                setMissionRecordedByDate((prev) => ({
                                  ...prev,
                                  [selectedMissionDateKey]: true,
                                }));
                                setMissionTextUnlockedByDate((prev) => ({
                                  ...prev,
                                  [selectedMissionDateKey]: false,
                                }));
                                await startRecording();
                              } else if (recState === 'recording') {
                                stopRecording();
                              } else if (recState === 'paused' || recState === 'textReady') {
                                missionTranscribeAttemptKeyRef.current = null;
                                setMissionTextUnlockedByDate((prev) => ({
                                  ...prev,
                                  [selectedMissionDateKey]: false,
                                }));
                                setMissionTextByDate((prev) => ({
                                  ...prev,
                                  [selectedMissionDateKey]: '',
                                }));
                                setAudioBlob(null);
                                setAudioR2Key(null);
                                setMissionTextEditingByDate((prev) => ({
                                  ...prev,
                                  [selectedMissionDateKey]: false,
                                }));
                                await startRecording();
                              }
                            }}
                          >
                            {recState === 'idle' && '녹음 시작'}
                            {recState === 'recording' && (
                              <>
                                <span className="db-rec-dot" aria-hidden />
                                종료 ({recSeconds}초)
                              </>
                            )}
                            {recState === 'paused' && '다시 녹음'}
                            {recState === 'textReady' && '다시 녹음'}
                          </button>
                          {transcribeError && (
                            <p
                              className="db-muted"
                              style={{ color: '#ef4444', marginTop: 6, fontSize: 13 }}
                            >
                              {transcribeError}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="db-mission-step">
                      <div className="db-mission-step-head">
                        <div className="db-mission-step-title-with-icon">
                          <span className="db-mission-step-inline-icon" aria-hidden>
                            <IconMissionFeedbackDoc size={20} />
                          </span>
                          <h3 className="db-mission-step-title">{missionTextSectionTitle}</h3>
                        </div>
                      </div>
                      <div className="db-mission-step-body">
                        {isTranscribing ? (
                          <div
                            className="db-mission-textarea db-mission-textarea--loading"
                            role="status"
                            aria-live="polite"
                            aria-label="음성을 텍스트로 변환하는 중"
                          >
                            <span className="db-mission-textarea-loading-dot" aria-hidden />
                            <span>텍스트로 변환하는 중…</span>
                          </div>
                        ) : (
                          <textarea
                            className={`db-textarea${isMissionTextEditable ? ' db-mission-textarea--editing' : ''}`}
                            value={selectedMissionText}
                            readOnly={!isMissionTextEditable}
                            onChange={(e) => setSelectedMissionText(e.target.value)}
                            placeholder={
                              isSelectedMissionComplete
                                ? ''
                                : !isMissionTextUnlocked
                                  ? missionFeedbackCopy.textBoxHintBefore
                                  : ''
                            }
                            rows={4}
                          />
                        )}
                        <div className="db-mission-step-actions">
                          <button
                            type="button"
                            className="db-btn db-btn--ghost"
                            disabled={
                              isSelectedMissionComplete || isTranscribing || !isMissionTextUnlocked
                            }
                            onClick={toggleMissionTextEditing}
                          >
                            {isMissionTextEditing ? '완료' : '수정'}
                          </button>
                          <button
                            type="button"
                            className="db-btn db-btn--primary"
                            disabled={
                              isSelectedMissionComplete ||
                              isTranscribing ||
                              isMissionTextEditing ||
                              !selectedMissionText.trim() ||
                              !hasSelectedRecording ||
                              !isMissionTextUnlocked
                            }
                            onClick={handleMissionSubmitClick}
                          >
                            공유
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </>
            )}

            {tab === 'meet' && (
              <section className="db-card db-card--flush db-card--meet">
                <div className="db-chat-header">
                  <h2 className="db-card-title db-card-title--inline">
                    {isSelfCohortSelected ? 'AI 슬립코치' : '그룹챗'}
                  </h2>
                  <button
                    type="button"
                    className="db-members-trigger"
                    onClick={() => setMembersOpen((v) => !v)}
                    aria-expanded={membersOpen}
                    aria-label={`구성원 ${visibleGroupMembers.length}명`}
                  >
                    <IconChatMembers />
                    <span className="db-badge" aria-hidden="true">
                      {visibleGroupMembers.length}
                    </span>
                  </button>
                </div>

                {membersOpen && (
                  <div className="db-members-panel">
                    <ul className="db-members-list">
                      {visibleGroupMembers.map((m) => (
                        <li key={m.id} className="db-members-item">
                          <img
                            className="db-members-avatar"
                            src={m.avatar_url}
                            alt=""
                            width={40}
                            height={40}
                            loading="lazy"
                          />
                          <div className="db-members-meta">
                            <div className="db-members-name-line">
                              <span className="db-members-name">{m.name}</span>
                              {m.role === 'master_coach' && (
                                <span className="db-msg-role-badge db-msg-role-badge--coach">
                                  마스터 코치
                                </span>
                              )}
                              {m.role === 'coach' && (
                                <span className="db-msg-role-badge db-msg-role-badge--coach">
                                  코치
                                </span>
                              )}
                              {m.role === 'manager' && (
                                <span className="db-msg-role-badge db-msg-role-badge--manager">
                                  매니저
                                </span>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="db-chat-body">
                  <p className="db-chat-reply-hint">
                    <span className="db-chat-reply-hint-label">공지</span>
                    {isSelfCohortSelected ? (
                      <>
                        메시지를 입력하면 <strong>AI 슬립코치</strong>가 순서대로 답변해줘요. 대화는{' '}
                        <strong>맨 아래</strong>에 시간 순서대로 쌓입니다.
                      </>
                    ) : (
                      <>
                        상대 말풍선에 마우스를 올리면 오른쪽 아래 <strong>답장</strong>이 보여요.
                        답장을 누른 뒤 입력하면 원글 인용이 붙은 채로 <strong>맨 아래</strong>에
                        시간 순서대로 쌓입니다.
                      </>
                    )}
                  </p>
                  <div className="db-chat-messages">
                    {chatLoading && chatMessages.length === 0 && (
                      <div
                        className="db-chat-loading"
                        role="status"
                        aria-live="polite"
                      >
                        <span className="db-spinner" aria-hidden />
                        <span className="db-chat-loading-text">
                          {isSelfCohortSelected
                            ? 'AI 슬립코치 대화를 불러오는 중이에요…'
                            : '그룹챗 메시지를 불러오는 중이에요…'}
                        </span>
                      </div>
                    )}
                    {chatMessages.map((msg) => {
                      const isSelf = Boolean(msg.author.isSelf);
                      return (
                        <article
                          key={msg.id}
                          className={`db-msg-block ${isSelf ? 'db-msg-block--self' : 'db-msg-block--peer'}`}
                        >
                          <div className="db-msg-block-surface">
                            <div
                              className={`db-msg-row ${isSelf ? 'db-msg-row--self' : 'db-msg-row--peer'}`}
                            >
                              {!isSelf && (
                                <img
                                  className="db-msg-avatar"
                                  src={msg.author.avatar_url}
                                  alt=""
                                  width={36}
                                  height={36}
                                  loading="lazy"
                                />
                              )}
                              <div className="db-msg-bubble-wrap">
                                <div className="db-msg-column">
                                  <div className="db-msg-meta">
                                    <div className="db-msg-meta-row">
                                      <div className="db-msg-meta-left">
                                        <span className="db-msg-name">{msg.author.name}</span>
                                        {msg.author.role === 'master_coach' && (
                                          <span className="db-msg-role-badge db-msg-role-badge--coach">
                                            마스터 코치
                                          </span>
                                        )}
                                        {msg.author.role === 'coach' && (
                                          <span className="db-msg-role-badge db-msg-role-badge--coach">
                                            코치
                                          </span>
                                        )}
                                        {msg.author.role === 'manager' && (
                                          <span className="db-msg-role-badge db-msg-role-badge--manager">
                                            매니저
                                          </span>
                                        )}
                                      </div>
                                      {msg.sentAt && (
                                        <time className="db-msg-time" dateTime={msg.sentAt}>
                                          {formatChatMessageTime(msg.sentAt)}
                                        </time>
                                      )}
                                    </div>
                                  </div>
                                  {msg.replyTo && (
                                    <div className="db-msg-quote">
                                      <span className="db-msg-quote-label">
                                        {msg.replyTo.authorName}에게 답장
                                      </span>
                                      <p className="db-msg-quote-body">{msg.replyTo.preview}</p>
                                    </div>
                                  )}
                                  <p className="db-msg-body">{msg.body}</p>
                                </div>
                                {!isSelf && !isSelfCohortSelected && (
                                  <button
                                    type="button"
                                    className="db-msg-reply-trigger"
                                    onClick={() =>
                                      setReplyingTo({
                                        id: msg.id,
                                        authorName: msg.author.name,
                                        preview: truncateChatPreview(msg.body),
                                      })
                                    }
                                  >
                                    답장
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                    {isSelfCohortSelected && coachReplyPending && (
                      <article
                        className="db-msg-block db-msg-block--peer db-msg-block--coach-typing"
                        aria-live="polite"
                      >
                        <div className="db-msg-block-surface">
                          <div className="db-msg-row db-msg-row--peer">
                            <img
                              className="db-msg-avatar"
                              src={coachProfile.avatar_url}
                              alt=""
                              width={36}
                              height={36}
                              loading="lazy"
                            />
                            <div className="db-msg-bubble-wrap">
                              <div className="db-msg-column">
                                <div className="db-msg-meta">
                                  <div className="db-msg-meta-row">
                                    <div className="db-msg-meta-left">
                                      <span className="db-msg-name">{coachProfile.name}</span>
                                      <span className="db-msg-role-badge db-msg-role-badge--coach">
                                        코치
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <p className="db-msg-body db-msg-body--typing">
                                  <span className="db-typing-dots" aria-hidden>
                                    <span />
                                    <span />
                                    <span />
                                  </span>
                                  <span className="db-typing-text">답변을 작성 중이에요…</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </article>
                    )}
                    {isSelfCohortSelected && coachReplyError && !coachReplyPending && (
                      <div className="db-coach-error" role="alert">
                        <span className="db-coach-error-text">
                          AI 슬립코치 응답을 받지 못했어요. 다시 시도해 주세요.
                        </span>
                        <button
                          type="button"
                          className="db-coach-error-retry"
                          onClick={retryCoachReply}
                        >
                          재시도
                        </button>
                      </div>
                    )}
                    <div ref={chatScrollEndRef} className="db-chat-scroll-end" aria-hidden />
                  </div>
                </div>

                <div className="db-chat-footer">
                  {replyingTo && (
                    <div className="db-chat-reply-bar" role="status">
                      <div className="db-chat-reply-bar-main">
                        <span className="db-chat-reply-bar-title">
                          <span className="db-chat-reply-bar-name">{replyingTo.authorName}</span>
                          에게 답장
                        </span>
                        <p className="db-chat-reply-bar-preview">{replyingTo.preview}</p>
                      </div>
                      <button
                        type="button"
                        className="db-chat-reply-bar-cancel"
                        onClick={() => setReplyingTo(null)}
                        aria-label="답장 취소"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {isCoachExpired && (
                    <p className="db-chat-expired-banner" role="note">
                      프로그램 기간이 종료되었어요. 기간을 연장하면 AI 슬립코치와 다시 대화할 수 있어요.
                    </p>
                  )}

                  <div className="db-chat-input-row">
                    <input
                      type="text"
                      className="db-input db-input--chat"
                      placeholder={
                        isCoachExpired
                          ? '프로그램 기간이 종료되었어요'
                          : replyingTo
                            ? '답장 메시지 입력'
                            : '메시지를 입력하세요'
                      }
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        if (e.nativeEvent?.isComposing || e.repeat) return;
                        e.preventDefault();
                        handleChatSend();
                      }}
                      disabled={isCoachExpired}
                      aria-label={replyingTo ? '답장 입력' : '메시지 입력'}
                    />
                    <button
                      type="button"
                      className="db-btn db-btn--primary db-btn--send"
                      disabled={chatSending || isCoachExpired}
                      onClick={handleChatSend}
                      aria-label={replyingTo ? '답장 전송' : '전송'}
                    >
                      <IconSend />
                    </button>
                  </div>
                </div>
              </section>
            )}

            {tab === 'my' && (
              <>
                <section className="db-card db-card--my" aria-labelledby="db-my-profile-title">
                  <header className="db-my-head">
                    <h2 id="db-my-profile-title" className="db-my-title">
                      프로필
                    </h2>
                    {/* <p className="db-my-lead">그룹챗에 표시되는 닉네임이에요.</p> */}
                  </header>

                  <div className="db-my-profile-block">
                    <div className="db-my-profile-photo-col">
                      <div className="db-my-avatar-ring" aria-hidden>
                        {profileAvatarUrl ? (
                          <img
                            src={profileAvatarUrl}
                            alt=""
                            className="db-my-avatar-img"
                            width={48}
                            height={48}
                            decoding="async"
                          />
                        ) : (
                          <div className="db-avatar-placeholder db-my-avatar-sm">
                            {(nickname || '?')[0]}
                          </div>
                        )}
                      </div>
                      {/* <button
                      type="button"
                      className="db-my-photo-link"
                      title="JPG, PNG · 권장 400×400px"
                    >
                      사진 변경
                    </button> */}
                    </div>
                    <div className="db-my-profile-field-col">
                      <label className="db-field db-field--flush" htmlFor="db-my-nickname">
                        <span className="db-label">닉네임</span>
                        <input
                          id="db-my-nickname"
                          type="text"
                          className={`db-input${profileErrors.nickname ? ' db-input--error' : ''}`}
                          value={nickname}
                          onChange={(e) => {
                            setNickname(e.target.value);
                            clearProfileError('nickname');
                          }}
                          autoComplete="nickname"
                          aria-invalid={!!profileErrors.nickname}
                          aria-describedby={
                            profileErrors.nickname ? 'db-my-nickname-error' : undefined
                          }
                        />
                        {profileErrors.nickname && (
                          <p id="db-my-nickname-error" className="db-field-error-msg" role="alert">
                            {profileErrors.nickname}
                          </p>
                        )}
                      </label>
                    </div>
                  </div>

                  <div className="db-my-section">
                    {/* <h3 className="db-my-section-title">추가 정보</h3> */}
                    <div className="db-my-grid">
                      <label className="db-field db-field--flush" htmlFor="db-my-gender">
                        <span className="db-label">
                          성별
                          <span className="db-label-required" aria-hidden="true">
                            *
                          </span>
                        </span>
                        <select
                          id="db-my-gender"
                          className={`db-input${profileErrors.gender ? ' db-input--error' : ''}`}
                          value={gender}
                          onChange={(e) => {
                            setGender(e.target.value);
                            clearProfileError('gender');
                          }}
                          aria-invalid={!!profileErrors.gender}
                          aria-describedby={profileErrors.gender ? 'db-my-gender-error' : undefined}
                        >
                          <option value="">선택</option>
                          <option value="f">여성</option>
                          <option value="m">남성</option>
                          <option value="x">기타 / 응답 안 함</option>
                        </select>
                        {profileErrors.gender && (
                          <p id="db-my-gender-error" className="db-field-error-msg" role="alert">
                            {profileErrors.gender}
                          </p>
                        )}
                      </label>
                      <label className="db-field db-field--flush" htmlFor="db-my-birth">
                        <span className="db-label">
                          생년월일
                          <span className="db-label-required" aria-hidden="true">
                            *
                          </span>
                        </span>
                        <input
                          id="db-my-birth"
                          type="date"
                          className={`db-input${profileErrors.birth ? ' db-input--error' : ''}`}
                          value={birth}
                          onChange={(e) => {
                            setBirth(profileBirthToDateInputValue(e.target.value));
                            clearProfileError('birth');
                          }}
                          aria-invalid={!!profileErrors.birth}
                          aria-describedby={profileErrors.birth ? 'db-my-birth-error' : undefined}
                        />
                        {profileErrors.birth && (
                          <p id="db-my-birth-error" className="db-field-error-msg" role="alert">
                            {profileErrors.birth}
                          </p>
                        )}
                      </label>
                      <label
                        className="db-field db-field--flush db-my-grid-full-row"
                        htmlFor="db-my-phone"
                      >
                        <span className="db-label">
                          휴대폰 번호
                          <span className="db-label-required" aria-hidden="true">
                            *
                          </span>
                        </span>
                        <input
                          id="db-my-phone"
                          type="tel"
                          className={`db-input${profileErrors.phone ? ' db-input--error' : ''}`}
                          value={formatKoreanPhoneDisplay(phone)}
                          onChange={(e) => {
                            setPhone(
                              normalizeKoreanPhoneDigits(
                                phoneOnlyDigits(e.target.value).slice(0, 11)
                              )
                            );
                            clearProfileError('phone');
                          }}
                          placeholder="010-0000-0000"
                          autoComplete="tel"
                          inputMode="numeric"
                          maxLength={13}
                          aria-invalid={!!profileErrors.phone}
                          aria-describedby={
                            profileErrors.phone
                              ? 'db-my-phone-error db-my-phone-purpose'
                              : 'db-my-phone-purpose'
                          }
                        />
                        {profileErrors.phone && (
                          <p id="db-my-phone-error" className="db-field-error-msg" role="alert">
                            {profileErrors.phone}
                          </p>
                        )}
                        <p id="db-my-phone-purpose" className="db-hint">
                          비밀번호 변경/계정 분실 시, 본인 인증에 활용돼요.
                        </p>
                      </label>
                      <div className="db-my-grid-full-row db-my-goal-in-profile">
                        <h3 id="db-my-goal-title" className="db-my-goal-title">
                          4주 변화 목표
                        </h3>
                        <div className="db-field db-field--flush">
                          <textarea
                            id="db-my-four-week-goal"
                            className={`db-textarea db-my-goal-textarea${profileErrors.fourWeekGoal ? ' db-textarea--error' : ''}`}
                            rows={5}
                            value={fourWeekChangeGoal}
                            onChange={(e) => {
                              setFourWeekChangeGoal(e.target.value);
                              clearProfileError('fourWeekGoal');
                            }}
                            placeholder="예) 규칙적인 수면 패턴이 만들어진다!"
                            aria-labelledby="db-my-goal-title"
                            aria-invalid={!!profileErrors.fourWeekGoal}
                            aria-describedby={
                              profileErrors.fourWeekGoal
                                ? 'db-my-four-week-goal-error db-my-four-week-goal-example'
                                : 'db-my-four-week-goal-example'
                            }
                          />
                          {profileErrors.fourWeekGoal && (
                            <p
                              id="db-my-four-week-goal-error"
                              className="db-field-error-msg"
                              role="alert"
                            >
                              {profileErrors.fourWeekGoal}
                            </p>
                          )}
                          <p id="db-my-four-week-goal-example" className="db-hint">
                            프로그램 종료 후, 얻게 될 변화를 미리 적어보세요.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="db-my-sticky-actions">
                    <button
                      type="button"
                      className="db-btn db-btn--primary db-btn--block"
                      onClick={handleProfileSave}
                      disabled={profileSaveBusy}
                    >
                      {profileSaveBusy ? '저장 중…' : '변경 사항 저장'}
                    </button>
                  </div>
                </section>

                <section className="db-card db-card--my" aria-labelledby="db-my-survey-title">
                  <header className="db-my-head">
                    <h2 id="db-my-survey-title" className="db-my-title">
                      설문 검사
                    </h2>
                  </header>

                  <div className="db-my-survey-pair" role="group" aria-label="사전·사후 설문">
                    <article className="db-my-survey-card">
                      <div className="db-my-survey-card-stack">
                        <div className="db-my-panel-icon db-my-survey-card-icon" aria-hidden>
                          <IconSurveyPreCard />
                        </div>
                        <div className="db-my-panel-text">
                          <h3 className="db-my-survey-card-title">사전 설문</h3>
                          <p className="db-my-panel-desc">
                            현재 수면 상태를 파악하기 위한 설문 검사로, 약 5~10분 정도 소요돼요.
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="db-btn db-btn--primary db-btn--block db-btn--survey-card"
                        onClick={() => {
                          if (isPreSurveyCompleted) {
                            setChangeReportPhase('pre');
                            setChangeReportModalOpen(true);
                            return;
                          }
                          setSurveyModalPhase('pre');
                          setSurveyModalOpen(true);
                        }}
                      >
                        {isPreSurveyCompleted ? '검사 결과 보기' : '검사하기'}
                      </button>
                    </article>
                    <article className="db-my-survey-card">
                      <div className="db-my-survey-card-stack">
                        <div className="db-my-panel-icon db-my-survey-card-icon" aria-hidden>
                          <IconSurveyPostCard />
                        </div>
                        <div className="db-my-panel-text">
                          <h3 className="db-my-survey-card-title">사후 설문</h3>
                          <p className="db-my-panel-desc">
                            {isChangeReportUnlocked
                              ? '사후 검사를 진행할 수 있어요.'
                              : `사후 검사는 ${changeReportDaysRemaining ?? '-'}일 뒤에 할 수 있어요. (${changeReportOpenDateLabel})`}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="db-btn db-btn--primary db-btn--block db-btn--survey-card"
                        disabled={!isChangeReportUnlocked}
                        title={
                          isChangeReportUnlocked
                            ? undefined
                            : '코호트 일정이 끝난 다음날부터 열려요.'
                        }
                        onClick={() => {
                          if (isPostSurveyCompleted) {
                            setChangeReportPhase('post');
                            setChangeReportModalOpen(true);
                            return;
                          }
                          setSurveyModalPhase('post');
                          setSurveyModalOpen(true);
                        }}
                      >
                        {isPostSurveyCompleted ? '변화 리포트 보기' : '검사하기'}
                      </button>
                    </article>
                  </div>
                </section>

                <section className="db-card db-card--my db-card--danger-zone">
                  <h2 className="db-my-danger-title">세션</h2>
                  <p className="db-my-danger-desc">로그아웃하면 이 기기에서 인증이 해제돼요.</p>
                  <button
                    type="button"
                    className="db-btn db-btn--logout db-btn--block"
                    onClick={handleLogout}
                  >
                    로그아웃
                  </button>
                </section>
              </>
            )}
          </main>
          {showProfileGate && (
            <div
              className="db-profile-gate-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="db-profile-gate-title"
              onClick={() => setTab('my')}
            >
              <div className="db-profile-gate-card" onClick={(e) => e.stopPropagation()}>
                <p id="db-profile-gate-title" className="db-profile-gate-title">
                  내 정보 탭을 완성해 주세요
                </p>
                <p className="db-profile-gate-lead">
                  프로필과 설문 검사를 모두 완료한 뒤 이완훈련과 그룹챗을 이용할 수 있어요.
                </p>
                <button
                  type="button"
                  className="db-btn db-btn--primary db-btn--block db-profile-gate-btn"
                  onClick={() => setTab('my')}
                >
                  내 정보로 이동
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <nav className="db-tabbar" role="tablist" aria-label="대시보드 메뉴">
        {tabItems.map((t) => {
          const Icon = t.Icon;
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`db-tab ${isActive ? 'db-tab--active' : ''}`}
              disabled={isMissionBusy}
              onClick={() => {
                if (isMissionBusy) return;
                setTab(t.id);
              }}
            >
              <span className="db-tab-icon-wrap">
                <Icon />
              </span>
              <span className="db-tab-label">{t.label}</span>
            </button>
          );
        })}
      </nav>

      {consentModalOpen && (
        <div
          className="db-modal-overlay"
          onClick={() => {
            setConsentModalOpen(false);
            setConsentChecked(false);
          }}
        >
          <div className="db-modal db-modal--consent" onClick={(e) => e.stopPropagation()}>
            <div className="db-modal-icon" aria-hidden>
              🎙️
            </div>
            <h3 className="db-modal-title">음성 데이터 수집·이용 동의</h3>

            <div className="db-consent-body">
              <dl className="db-consent-dl">
                <dt>수집 항목</dt>
                <dd>코칭 후기 음성 녹음 데이터, 변환 텍스트</dd>

                <dt>수집·이용 목적</dt>
                <dd>
                  <ol className="db-consent-ol">
                    <li>음성을 텍스트로 변환하여 코칭 후기로 등록</li>
                    <li>텍스트 변환 및 서비스 운영을 위한 음성 원본 보관</li>
                    <li>음성인식(STT) 정확도 개선</li>
                    <li>수면 분석 알고리즘 고도화</li>
                  </ol>
                </dd>

                <dt>보관 기간</dt>
                <dd>
                  음성 원본: 회원 탈퇴 시 또는 삭제 요청 시까지
                  <br />
                  비식별화 음성(AI 학습용): 수집일로부터 2년 또는 회원 탈퇴 시 (먼저 도래하는 시점)
                </dd>

                <dt>보관 방식</dt>
                <dd>
                  AES-256 암호화 적용하여 보안 저장소에 보관
                  <br />
                  AI 학습 활용 시 비식별화 처리(개인 식별 정보 제거) 후 활용
                </dd>

                <dt>제3자 제공</dt>
                <dd>
                  텍스트 변환을 위해 NAVER Cloud(CLOVA Speech)에 음성 데이터 전송 (변환 완료 즉시
                  파기)
                </dd>

                <dt>삭제 안내</dt>
                <dd>
                  서비스 내 별도 저장 내역 확인 기능은 제공되지 않습니다. 삭제를 원하시는 경우
                  이메일(<span className="db-consent-email">cs@s-omni.com</span>) 또는 고객센터로
                  문의해 주시면 영업일 기준 5일 이내에 삭제 처리됩니다.
                </dd>
              </dl>

              <p className="db-consent-notice">
                동의를 거부할 권리가 있으며, 거부 시 음성 후기 기능 이용이 제한됩니다.
              </p>
            </div>

            <label className="db-consent-check">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
              <span>위 내용을 모두 확인하였으며, 동의합니다</span>
            </label>

            <div className="db-modal-actions">
              <button
                type="button"
                className="db-btn db-modal-btn-cancel"
                onClick={() => {
                  setConsentModalOpen(false);
                  setConsentChecked(false);
                }}
              >
                거부
              </button>
              <button
                type="button"
                className="db-btn db-btn--primary"
                disabled={!consentChecked}
                onClick={handleConsentAgree}
              >
                동의하고 계속
              </button>
            </div>
          </div>
        </div>
      )}

      <DashboardSurveyModal
        open={surveyModalOpen}
        onClose={() => setSurveyModalOpen(false)}
        userId={currentUserId}
        cohortCareOptionId={selectedCohortCareOptionId}
        phase={surveyModalPhase}
      />

      <DashboardChangeReportModal
        open={changeReportModalOpen}
        onClose={() => setChangeReportModalOpen(false)}
        userId={currentUserId}
        cohortCareOptionId={selectedCohortCareOptionId}
        phase={changeReportPhase}
      />

      {confirmModalOpen && (
        <div className="db-modal-overlay" onClick={() => setConfirmModalOpen(false)}>
          <div className="db-modal" onClick={(e) => e.stopPropagation()}>
            <div className="db-modal-icon" aria-hidden>
              ✉️
            </div>
            <h3 className="db-modal-title">
              {isSelfCohortSelected
                ? 'AI 슬립코치에게 공유하시겠습니까?'
                : '그룹챗에 공유하시겠습니까?'}
            </h3>
            <p className="db-modal-desc">공유 후에는 수정 및 재녹음 할 수 없습니다.</p>
            <p className="db-modal-date">{formatMissionDateLabel(selectedMissionDateKey)}</p>
            <div className="db-modal-actions">
              <button
                type="button"
                className="db-btn db-modal-btn-cancel"
                onClick={() => setConfirmModalOpen(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="db-btn db-btn--primary"
                onClick={handleMissionSubmitConfirm}
              >
                공유
              </button>
            </div>
          </div>
        </div>
      )}

      {coachRedirectModalOpen && (
        <div className="db-modal-overlay" onClick={handleCoachRedirectClose}>
          <div className="db-modal" onClick={(e) => e.stopPropagation()}>
            <div className="db-modal-icon" aria-hidden>
              💬
            </div>
            <h3 className="db-modal-title">피드백을 잘 받았어요!</h3>
            <p className="db-modal-desc">
              AI 슬립코치가 답변을 작성 중이에요.
              <br />
              잠시 후 AI 슬립코치 탭으로 이동할게요.
            </p>
            <div className="db-modal-actions">
              <button
                type="button"
                className="db-btn db-btn--primary"
                onClick={handleCoachRedirectClose}
              >
                지금 이동하기
              </button>
            </div>
          </div>
        </div>
      )}

      {weekInfoModal.open && (
        <div
          className="db-modal-overlay"
          onClick={() => setWeekInfoModal({ open: false, title: '', information: '' })}
        >
          <div className="db-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="db-modal-title">{weekInfoModal.title}</h3>
            <p className="db-modal-desc db-modal-desc--week-info">{weekInfoModal.information}</p>
            <div className="db-modal-actions">
              <button
                type="button"
                className="db-btn db-modal-btn-cancel"
                onClick={() => setWeekInfoModal({ open: false, title: '', information: '' })}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
